import { Product } from "../components/productTokens";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, Clock, Briefcase, Shield,
  AlertTriangle, Zap, ArrowRight, Send,
  MessageSquare, Lock, Landmark,
  Plus, Trash2, Layers, X, ChevronRight,
} from "lucide-react";
import PaymentConfirmModal from "../components/PaymentConfirmModal";
import PaymentMethodFeeCompare from "../components/PaymentMethodFeeCompare";
import FullCostQuote, { payableMethods } from "../components/FullCostQuote";
import { ErrorState, SecondaryButton } from "../components/customerUi";
import { trackStartTransaction } from "../utils/analytics";
import { confirmedSettlementAt, resolveSmartDealFinancialPresentation, resolveSmartDealProgress } from "../components/transactionState";
import { API_URL } from "../utils/api";
import { publicTransactionUrl, copyTransactionUrl } from "../utils/transactionSharing";
import { calculateConfiguredPlatformFee, usePlatformConfig } from "../context/PlatformConfigContext";

// Minimum escrow amount — must match the backend (settings.MINIMUM_TRANSACTION_AMOUNT).
const MIN_TRANSACTION_AMOUNT = 500;
const MIN_TRANSACTION_MESSAGE = `Minimum transaction amount is R${MIN_TRANSACTION_AMOUNT} to cover processing fees.`;

// Delivery window bounds — must match the backend check in routes/smart_deals.py.
const MIN_DELIVERY_DAYS = 1;
const MAX_DELIVERY_DAYS = 60;
const DELIVERY_DAYS_MESSAGE = `Delivery days must be between ${MIN_DELIVERY_DAYS} and ${MAX_DELIVERY_DAYS}`;

// Shared dark TrustTrade app palette (matches components/designTokens.js VDark —
// kept as a local literal object since this file predates that shared module and
// every color reference below goes through this D.xxx indirection).
const D = { ...Product, surfaceHi: Product.hover, borderLight: Product.border, textMuted: Product.sub, textSoft: Product.dim, blue: Product.accent, warning: Product.warn, danger: Product.error, orange: Product.warn, purple: "#A78BFA" };

const STATUS = {
  PENDING:         { label: "Awaiting agreement",                      color: D.warning, bg: "rgba(245,171,31,0.14)", dot: D.warning },
  ACCEPTED:        { label: "Awaiting payment",                        color: D.accent,  bg: "rgba(47,155,255,0.14)", dot: D.accent },
  PAYMENT_PENDING: { label: "Awaiting payment",                        color: D.accent,  bg: "rgba(47,155,255,0.14)", dot: "#2F9BFF" },
  FUNDED:          { label: "Payment Protected",                       color: D.success, bg: "rgba(61,209,148,0.14)", dot: D.success },
  DELIVERED:       { label: "Awaiting client confirmation",            color: D.purple,  bg: "rgba(139,92,246,0.14)", dot: D.purple },
  APPROVED:        { label: "Payout pending confirmation",             color: D.success, bg: "rgba(61,209,148,0.14)", dot: D.success },
  COMPLETE:        { label: "Payout pending confirmation",             color: D.success, bg: "rgba(61,209,148,0.14)", dot: D.success },
  DISPUTED:        { label: "Disputed / protection hold",              color: D.danger,  bg: "rgba(240,102,92,0.14)", dot: D.danger },
  // Milestone-deal (parent) statuses:
  PROPOSED:          { label: "Awaiting approval",        color: D.warning, bg: "rgba(245,171,31,0.14)", dot: D.warning },
  STRUCTURE_APPROVED:{ label: "Approved — pay first stage", color: D.accent, bg: "rgba(47,155,255,0.14)", dot: D.accent },
  IN_PROGRESS:       { label: "In progress",              color: D.accent, bg: "rgba(47,155,255,0.12)", dot: D.accent },
  CLOSED:            { label: "Project closed",           color: D.textMuted, bg: D.surfaceHi, dot: D.textSoft },
  CANCELLED:         { label: "Closed before payment",    color: D.textMuted, bg: D.surfaceHi, dot: D.textSoft },
};

// Per-milestone status chips (milestone deals).
// Premium milestone-page redesign (2026-10-13): one consistent short-wording
// table across the whole product, no rainbow of state colours. Blue is the
// "something to look at / in progress" colour; green is reserved for
// confirmed-protected/confirmed-released; red never appears here (it only
// ever appears inside the opened dispute panel, see CurrentStageCard).
// Purple is removed from status styling entirely — DELIVERED now reads as
// an accent (blue) "needs your attention" state, not a distinct 5th colour.
const MS_STATUS = {
  PROPOSED:        { label: "Upcoming",            color: D.textSoft,  dot: D.textSoft },
  AWAITING_PAYMENT:{ label: "Awaiting funding",     color: D.accent,    dot: D.accent },
  PAYMENT_PENDING: { label: "Payment processing",   color: D.accent,    dot: D.accent },
  FUNDED:          { label: "Payment protected",    color: D.success,   dot: D.success },
  DELIVERED:       { label: "Ready for review",     color: D.accent,    dot: D.accent },
  RELEASED:        { label: "Payment released",     color: D.success,   dot: D.success },
  // A dispute resolved in the buyer's favour — this stage is finished but
  // must never read as a successful release: neutral/muted, not green.
  REFUNDED:        { label: "Refunded",             color: D.textMuted, dot: D.textMuted },
  CANCELLED:       { label: "Closed",               color: D.textMuted, dot: D.textMuted },
  DISPUTED:        { label: "Under review",         color: D.danger,    dot: D.danger },
};

// RELEASED, REFUNDED, and CANCELLED are terminal, matching
// _MILESTONE_TERMINAL_STATUSES in routes/smart_deals.py. CANCELLED is an
// unpaid stage the parties closed — finished, but not a payout. Only a
// project with no refunds and no closed unpaid stages is "complete".
const MS_TERMINAL_STATUSES = new Set(["RELEASED", "REFUNDED", "CANCELLED"]);

// EFT temporarily disabled — see routes/tradesafe.py get_tradesafe_payment_url
// for why (TradeSafe can report FUNDS_DEPOSITED before real settlement).
// Card/Ozow method selection now goes through PaymentMethodFeeCompare
// (Final Banking Status, Fee & Chat Cleanup, 2026-10-14), which carries its
// own labels and never invents a processing fee — see
// paymentMethodFeeCompareCalc.js's three-tier confirmed/documented/unknown
// model for how a real, sourced rate is shown only once TradeSafe has
// actually provided one.

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem("session_token");
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Unknown error" }));
    const detail = err.detail;
    let msg;
    if (typeof detail === "string") msg = detail;
    else if (Array.isArray(detail)) msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join("; ");
    else if (detail && typeof detail === "object") msg = detail.message || detail.msg || JSON.stringify(detail);
    else msg = err.message || "Request failed";
    throw new Error(msg);
  }
  return res.json();
}

// ─── Shared style helpers ──────────────────────────────────────────────────

const card = (extra = {}) => ({
  background: D.surface,
  border: `1px solid ${D.border}`,
  borderRadius: 14,
  padding: "20px 22px",
  marginBottom: 14,
  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
  ...extra,
});

const btn = (bg, color = "#16181B", extra = {}) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  gap: 7, padding: "11px 20px", borderRadius: 9, border: "none",
  background: bg, color, fontSize: 13, fontWeight: 600,
  cursor: "pointer", transition: "opacity 0.15s", fontFamily: "inherit",
  whiteSpace: "nowrap", ...extra,
});

const label = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: D.textSoft, textTransform: "uppercase",
  letterSpacing: "0.07em", marginBottom: 6,
};

const input = {
  width: "100%", height: 40, padding: "0 12px", borderRadius: 8,
  border: `1px solid ${D.border}`, fontSize: 13, color: D.text,
  background: D.bg, outline: "none", boxSizing: "border-box",
  fontFamily: "inherit", marginBottom: 14,
};

const textarea = {
  width: "100%", padding: "10px 12px", borderRadius: 8,
  border: `1px solid ${D.border}`, fontSize: 13, color: D.text,
  background: D.bg, outline: "none", boxSizing: "border-box",
  fontFamily: "inherit", resize: "vertical", minHeight: 88,
  marginBottom: 14,
};

const Spinner = ({ size = 13 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    border: `2px solid rgba(255,255,255,0.2)`,
    borderTopColor: "#16181B",
    animation: "spin 0.7s linear infinite",
    flexShrink: 0,
  }} />
);

// ─── StatusBadge ──────────────────────────────────────────────────────────

// Milestone Deals list fix (2026-10-14): STATUS.PROPOSED and
// STATUS.STRUCTURE_APPROVED were a single shared label regardless of who was
// looking — a seller who had already sent the proposal saw "Awaiting
// approval" (their own wording for THEIR pending action), and once the
// client approved, BOTH parties saw "Approved — pay first stage" even
// though only the client has anything to pay. resolveSmartDealFinancialPresentation
// doesn't cover these two states (they're pre-funding agreement states, not
// financial/settlement ones), so this is the role-aware override for them
// specifically. STRUCTURE_APPROVED only ever reflects the narrow window
// between approval and the first funding attempt — the backend sets
// milestone 1 to AWAITING_PAYMENT in the very same request that sets
// deal.status to STRUCTURE_APPROVED (routes/smart_deals.py::approve_structure),
// and flips deal.status to IN_PROGRESS as soon as funding is attempted — so
// "ready to pay" here is never shown once funding is actually underway.
const ROLE_AWARE_DEAL_STATUS_LABELS = {
  PROPOSED: { buyer: "Awaiting your approval", seller: "Awaiting client approval" },
  STRUCTURE_APPROVED: { buyer: "Stage 1 ready to pay", seller: "Awaiting client payment" },
};

function StatusBadge({ status, record, role }) {
  const financial = resolveSmartDealFinancialPresentation(record || { status }, { role });
  const fallback = STATUS[status] || { label: status, color: D.textMuted, bg: D.surface, dot: D.textSoft };
  const roleAwareLabel = ROLE_AWARE_DEAL_STATUS_LABELS[status]?.[role === 'seller' ? 'seller' : 'buyer'];
  const s = financial
    ? { ...fallback, label: financial.label }
    : roleAwareLabel
      ? { ...fallback, label: roleAwareLabel }
      : fallback;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 600, padding: "3px 10px",
      borderRadius: 20, background: s.bg, color: s.color,
      border: `1px solid ${s.dot}33`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─── ProgressTracker ──────────────────────────────────────────────────────

function ProgressTracker({ status, record, role }) {
  const progress = resolveSmartDealProgress(record || { status }, { role });
  const steps = progress.steps;
    const stepLabels = { PENDING: "Agreement", ACCEPTED: "Payment", FUNDED: "Payment Protected", DELIVERED: "Confirm", COMPLETE: progress.finalLabel };
  const cur = progress.current;
  const curIdx = steps.indexOf(cur);
  const lastDone = progress.finalComplete && cur === "COMPLETE";
  return (
    <div style={{ display: "flex", alignItems: "center", padding: "16px 18px", background: D.surfaceHi, borderRadius: 12, marginBottom: 14, border: `1px solid ${D.border}` }}>
      {steps.map((step, i) => {
        const isLast = step === "COMPLETE";
        const done = isLast ? lastDone : i < curIdx;
        const current = !done && i === curIdx;
        return (
        <div key={step} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : undefined }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flexShrink: 0 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700,
              background: done ? D.success : current ? D.blue : D.surfaceHi,
              color: done || current ? "#16181B" : D.textSoft,
              border: `2px solid ${done ? D.success : current ? D.blue : D.border}`,
            }}>
              {done ? <CheckCircle size={13} /> : i + 1}
            </div>
            <span style={{ fontSize: 10, fontWeight: 600, color: done || current ? D.text : D.textSoft, whiteSpace: "nowrap" }}>
              {stepLabels[step]}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, margin: "0 6px", marginBottom: 20,
              background: i < curIdx ? D.success : D.border, borderRadius: 2,
            }} />
          )}
        </div>
      );})}
    </div>
  );
}

// ─── InfoRow ──────────────────────────────────────────────────────────────

function InfoRow({ label: lbl, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: `1px solid ${D.surfaceHi}` }}>
      <span style={{ fontSize: 12, color: D.textMuted }}>{lbl}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: D.text, fontFamily: mono ? "ui-monospace, monospace" : "inherit", textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>
        {value}
      </span>
    </div>
  );
}

// ─── ActionCard ───────────────────────────────────────────────────────────

function ActionCard({ accent, children }) {
  return (
    <div style={{
      background: D.surface, border: `1px solid ${accent}44`,
      borderLeft: `3px solid ${accent}`, borderRadius: 14,
      padding: "18px 20px", marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

// ─── EftDetailsCard ───────────────────────────────────────────────────────
// Bank-transfer details + reference, with a copy button per field. Rendered from
// the deal's stored eft_details so it survives the status flip to PAYMENT_PENDING
// (FundPanel unmounts on that flip — the buyer must always be able to see where to pay).

function EftDetailsCard({ details, fallbackAmount }) {
  const [copiedField, setCopiedField] = useState(null);
  const fmt = v => `R ${Number(v).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

  const copy = (key, value) => {
    navigator.clipboard.writeText(String(value)).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(k => (k === key ? null : k)), 1500);
    });
  };

  const rows = [
    ["Bank", details.bank],
    ["Account name", details.account_name],
    ["Account number", details.account_number],
    ["Branch code", details.branch_code],
    ["Reference", details.reference],
    ["Amount to pay", fmt(details.amount ?? fallbackAmount ?? 0)],
  ];

  return (
    <ActionCard accent={D.blue}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Landmark size={15} color={D.accent} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Pay via EFT bank transfer</h3>
      </div>
      <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 16px", lineHeight: 1.5 }}>
        {details.instructions || "Use this reference number when making your EFT payment. Funds will be confirmed within 1–2 business days."}
      </p>
      <div style={{ background: D.bg, border: `1px solid ${D.border}`, borderRadius: 10, padding: "6px 14px", marginBottom: 14 }}>
        {rows.map(([lbl, value]) => (
          <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${D.border}` }}>
            <span style={{ fontSize: 12, color: D.textMuted }}>{lbl}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: D.text, fontFamily: D.sans }}>{value || "—"}</span>
              {value && (
                <button
                  type="button"
                  onClick={() => copy(lbl, value)}
                  style={{ fontSize: 11, color: copiedField === lbl ? D.success : D.accent, background: "none", border: "none", cursor: "pointer", padding: 0, minWidth: 34, textAlign: "right" }}
                >
                  {copiedField === lbl ? "Copied" : "Copy"}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: D.warning, background: "rgba(245,158,11,0.14)", border: `1px solid ${D.warning}44`, borderRadius: 8, padding: "10px 12px", margin: 0 }}>
        Use the reference <strong>exactly as shown</strong>. This deal stays in <strong>Awaiting Payment</strong> until the funds are confirmed (1–2 business days).
      </p>
    </ActionCard>
  );
}

// ─── FundPanel ────────────────────────────────────────────────────────────

function FundPanel({ deal }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [payConfirm, setPayConfirm] = useState(null);  // { link, total_value, processing_fee }
  const [err, setErr] = useState(null);
  const [eftDetails, setEftDetails] = useState(null);

  const { config: platformConfig } = usePlatformConfig();
  // Stored deal values stay authoritative. Only legacy/incomplete records fall
  // back to the centrally configured platform fee.
  const platformFee = deal.platform_fee != null
    ? Number(deal.platform_fee)
    : calculateConfiguredPlatformFee(deal.amount, platformConfig.platform_fee_percent);
  const clientPaysPlatformFee = (deal.fee_paid_by || "CLIENT").toUpperCase() !== "FREELANCER";
  // deal.total may include a pre-payment processing-fee guess. The Card/Ozow
  // button does not print it. The authoritative payable amount is the checkout quote.
  const totalBeforeProviderFee = Math.round((Number(deal.amount) + (clientPaysPlatformFee ? platformFee : 0)) * 100) / 100;
  const eftFallbackAmount = deal.total != null
    ? Number(deal.total)
    : totalBeforeProviderFee;

  async function handleFund() {
    setLoading(true); setErr(null);
    try {
      if (method !== "eft") {
        navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&method=${encodeURIComponent(method)}`);
        return;
      }
      const res = await apiFetch(`/api/smart-deals/${deal.deal_id}/fund`, {
        method: "POST",
        body: JSON.stringify({ payment_method: method }),
      });
      if (res.eft_details) {
        setEftDetails(res.eft_details);
        return;
      }
      setErr(res.message || "Couldn't start the payment. Please try again or pick another method.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  const confirmAndPay = () => { const pc = payConfirm; if (!pc) return; setPayConfirm(null); window.location.href = pc.link; };

  if (eftDetails) {
    return <EftDetailsCard details={eftDetails} fallbackAmount={eftFallbackAmount} />;
  }

  return (
    <>
    <PaymentConfirmModal open={!!payConfirm} amount={payConfirm?.total_value} processingFee={payConfirm?.processing_fee} onConfirm={confirmAndPay} onCancel={() => setPayConfirm(null)} />
    <ActionCard accent={D.blue}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Lock size={15} color={D.blue} />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Pay safely</h3>
      </div>
      <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 16px", lineHeight: 1.5 }}>
          Your payment is securely held by our third-party payment provider until you approve the work. Bank payout can take up to 2 business days.
      </p>

      {/* Payment method selection + fee breakdown — one combined view
          (Final Banking Status, Fee & Chat Cleanup, 2026-10-14). Shows the
          deal amount, TrustTrade fee, and the processing fee for Card/Ozow
          wherever the payment provider has an actual sourced rate
          (account-confirmed, else the provider's own published-but-
          unreconciled rate, else "Confirmed at secure checkout" with no
          invented number) — see paymentMethodFeeCompareCalc.js for the full
          three-tier model this reuses unchanged from TransactionDetail.js. */}
      <div style={{ marginBottom: 14 }}>
        <PaymentMethodFeeCompare
          itemAmount={deal.amount}
          trustTradeFee={clientPaysPlatformFee ? platformFee : 0}
          trustTradeFeePercent={platformConfig.platform_fee_percent}
          baseBeforeProvider={totalBeforeProviderFee}
          confirmedRates={platformConfig.payment_method_processing_fee_confirmed || {}}
          documentedRates={platformConfig.payment_method_processing_rate_documented || {}}
          estimateReconciled={platformConfig.payment_method_documented_estimate_reconciled || {}}
          quickSettlementFee={platformConfig.provider_quick_settlement_fee}
          estimateEligible={clientPaysPlatformFee}
          vatPercent={platformConfig.south_africa_vat_percent}
          selectedMethod={method}
          onSelect={setMethod}
        />
        <p style={{ fontSize: 11, color: D.textSoft, margin: "10px 0 0" }}>
          The exact final amount is always confirmed at secure checkout, before you authorise payment.
        </p>
      </div>

      {err && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, color: D.danger, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      )}

      <button
        onClick={handleFund}
        disabled={loading}
        style={{ ...btn(D.blue), width: "100%", opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}
      >
        {loading ? <><Spinner /> Setting up payment…</> : <><Lock size={14} /> Continue to secure payment</>}
      </button>
    </ActionCard>
    </>
  );
}

// ─── MessageThread ────────────────────────────────────────────────────────

function MessageThread({ dealId, messages, currentUserId }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [localMessages, setLocalMessages] = useState(messages);
  const bottomRef = useRef(null);
  const prevCountRef = useRef(messages.length);

  useEffect(() => { setLocalMessages(messages); }, [messages]);

  // Only scroll to bottom when message count grows (new message), not on every refresh
  useEffect(() => {
    if (localMessages.length > prevCountRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevCountRef.current = localMessages.length;
  }, [localMessages]);

  async function send() {
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      const msg = await apiFetch(`/api/smart-deals/${dealId}/messages`, {
        method: "POST",
        body: JSON.stringify({ content }),
      });
      setText("");
      setLocalMessages(prev => [...prev, msg]);
    } catch {
      // silently ignore — user can retry
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={card()}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <MessageSquare size={14} color={D.accent} />
        <h3 style={{ fontSize: 13, fontWeight: 700, color: D.text, margin: 0, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Messages
        </h3>
      </div>

      {localMessages.length === 0 && (
        <p style={{ fontSize: 12, color: D.textSoft, textAlign: "center", padding: "16px 0", margin: 0 }}>
          No messages yet — start the conversation.
        </p>
      )}

      <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, marginBottom: localMessages.length ? 12 : 0 }}>
        {localMessages.map((m, i) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div key={m.message_id || i} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
              <div style={{
                background: mine ? D.blue : D.surfaceHi,
                border: `1px solid ${mine ? D.blue + "66" : D.border}`,
                borderRadius: mine ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                padding: "8px 12px", maxWidth: "75%",
              }}>
                {!mine && (
                  <p style={{ fontSize: 10, fontWeight: 600, color: D.accent, margin: "0 0 3px" }}>{m.sender_name || m.sender_email}</p>
                )}
                <p style={{ fontSize: 13, color: mine ? "#16181B" : D.text, margin: 0, lineHeight: 1.4, wordBreak: "break-word" }}>{m.content}</p>
                <p style={{ fontSize: 10, color: mine ? "rgba(255,255,255,0.75)" : D.textMuted, margin: "3px 0 0", textAlign: mine ? "right" : "left" }}>
                  {new Date(m.sent_at).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <input
          aria-label="Message"
          className="tt-smart-deal-message-input"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          style={{ ...input, flex: 1, height: 40, marginBottom: 0 }}
        />
        <button
          aria-label="Send message"
          onClick={send}
          disabled={sending || !text.trim()}
          style={{ ...btn(D.blue, "#16181B", { height: 40, padding: "0 16px", opacity: (sending || !text.trim()) ? 0.5 : 1 }) }}
        >
          {sending ? <Spinner /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateSmartDeal
// ─────────────────────────────────────────────────────────────────────────────

export function CreateSmartDeal() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", amount: "",
    freelancer_email: "", days_to_deliver: "", fee_paid_by: "CLIENT",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.title.trim()) e.title = "Required";
    if (!form.description.trim()) e.description = "Required";
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0) e.amount = "Enter a valid amount";
    else if (Number(form.amount) < MIN_TRANSACTION_AMOUNT) e.amount = MIN_TRANSACTION_MESSAGE;
    if (!form.freelancer_email.includes("@")) e.freelancer_email = "Enter a valid email";
    // Empty defaults to 1; otherwise must be a whole number from 1 to 60.
    if (form.days_to_deliver !== "") {
      const days = Number(form.days_to_deliver);
      if (!Number.isInteger(days) || days < MIN_DELIVERY_DAYS || days > MAX_DELIVERY_DAYS) {
        e.days_to_deliver = DELIVERY_DAYS_MESSAGE;
      }
    }
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true); setApiError(null);
    try {
      const days_to_deliver = form.days_to_deliver === "" ? 1 : Number(form.days_to_deliver);
      const res = await apiFetch("/api/smart-deals/", {
        method: "POST",
        body: JSON.stringify({ ...form, amount: Number(form.amount), days_to_deliver }),
      });
      navigate(`/smart-deals/${res.deal_id}`);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const fieldErr = k => errors[k] ? <p style={{ fontSize: 11, color: D.danger, margin: "2px 0 8px" }}>{errors[k]}</p> : null;
  const borderErr = k => errors[k] ? D.danger : D.border;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", paddingBottom: 40 }}>
      <Link to="/smart-deals" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: D.textMuted, textDecoration: "none", marginBottom: 20 }}>
        <ArrowLeft size={14} /> All deals
      </Link>

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: D.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Briefcase size={17} color="#16181B" />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: D.text, margin: 0 }}>New single-payment deal</h1>
        </div>
        <p style={{ fontSize: 13, color: D.textMuted, margin: 0 }}>
          Your payment is securely held by our third-party payment provider until you approve the work. Bank payout can take up to 2 business days.
        </p>
      </div>

      {apiError && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, borderLeft: `3px solid ${D.danger}`, color: D.danger, fontSize: 13, marginBottom: 14 }}>
          {apiError}
        </div>
      )}

      <div style={card()}>
        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-1" style={label}>Deal Title</label>
          <input id="deal-field-1" style={{ ...input, borderColor: borderErr("title") }} placeholder="e.g. Logo design for Acme Co" value={form.title} onChange={set("title")} />
          {fieldErr("title")}
        </div>

        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-2" style={label}>Scope of Work</label>
          <textarea id="deal-field-2" style={{ ...textarea, borderColor: borderErr("description") }} placeholder="Describe exactly what will be delivered…" value={form.description} onChange={set("description")} />
          {fieldErr("description")}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 4 }}>
          <div>
            <label htmlFor="deal-field-3" style={label}>Amount (ZAR)</label>
            <input id="deal-field-3" style={{ ...input, fontFamily: D.sans, fontWeight: 600, borderColor: borderErr("amount") }} type="number" min={MIN_TRANSACTION_AMOUNT} placeholder="5 000" value={form.amount} onChange={set("amount")} />
            {fieldErr("amount") || <p style={{ fontSize: 11, color: D.textMuted, margin: "2px 0 8px" }}>Minimum R{MIN_TRANSACTION_AMOUNT}.</p>}
          </div>
          <div>
            <label htmlFor="deal-field-4" style={label}>Days to Deliver</label>
            <input id="deal-field-4" style={{ ...input, borderColor: borderErr("days_to_deliver") }} type="number" min={MIN_DELIVERY_DAYS} max={MAX_DELIVERY_DAYS} step={1} placeholder="7" value={form.days_to_deliver} onChange={set("days_to_deliver")} />
            {fieldErr("days_to_deliver") || <p style={{ fontSize: 11, color: D.textMuted, margin: "2px 0 8px" }}>1–{MAX_DELIVERY_DAYS} days (defaults to 1).</p>}
          </div>
        </div>

        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-5" style={label}>Freelancer Email</label>
          <input id="deal-field-5" style={{ ...input, borderColor: borderErr("freelancer_email") }} type="email" placeholder="freelancer@example.com" value={form.freelancer_email} onChange={set("freelancer_email")} />
          {fieldErr("freelancer_email")}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ ...label, marginBottom: 10 }}>Who pays the TrustTrade fee?</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { value: "CLIENT",     title: "I pay (client)",    desc: "Fee added to your payment"      },
              { value: "FREELANCER", title: "Freelancer pays",   desc: "Fee deducted from payout"       },
            ].map(opt => {
              const active = form.fee_paid_by === opt.value;
              return (
                <button type="button" aria-pressed={active} key={opt.value} onClick={() => setForm(f => ({ ...f, fee_paid_by: opt.value }))} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "12px 14px", borderRadius: 10,
                  border: `1.5px solid ${active ? D.blue : D.border}`,
                  background: active ? "rgba(59,130,246,0.14)" : D.surfaceHi, cursor: "pointer",
                }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                    border: `2px solid ${active ? D.blue : D.borderLight}`,
                    background: active ? D.blue : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {active && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#16181B" }} />}
                  </div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: D.text, margin: "0 0 2px" }}>{opt.title}</p>
                    <p style={{ fontSize: 11, color: D.textMuted, margin: 0 }}>{opt.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(59,130,246,0.10)", border: `1px solid ${D.accent}33`, marginBottom: 18 }}>
          <Shield size={15} color={D.accent} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 12, color: D.textMuted, margin: 0, lineHeight: 1.5 }}>
            Your freelancer accepts, you pay (the payment is protected), they do the work, and you approve to release funds. A dispute pauses the payout.
          </p>
        </div>

        <button onClick={handleSubmit} disabled={loading} style={{ ...btn(D.accent, "#16181B"), width: "100%", opacity: loading ? 0.6 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? <><Spinner /> Creating…</> : <><Zap size={14} /> Create single-payment deal</>}
        </button>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input:focus,textarea:focus{border-color:${D.blue}!important;box-shadow:0 0 0 3px ${D.blue}22;} *{box-sizing:border-box}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SmartDealDetail
// ─────────────────────────────────────────────────────────────────────────────

export function SmartDealDetail() {
  const { dealId } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [approving, setApproving] = useState(false);
  const [disputing, setDisputing] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([
        apiFetch(`/api/smart-deals/${dealId}`),
        apiFetch("/api/auth/me"),
      ]);
      setDeal(d); setCurrentUser(u);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5s while deal is in an active state
  useEffect(() => {
    const ACTIVE = new Set([
      "PENDING", "ACCEPTED", "PAYMENT_PENDING", "FUNDED", "DELIVERED",
      // Milestone deals: keep polling while a milestone is being funded/worked.
      "PROPOSED", "STRUCTURE_APPROVED", "IN_PROGRESS",
    ]);
    if (!deal || !ACTIVE.has(deal.status)) return;
    const id = setInterval(() => {
      const scrollY = window.scrollY;
      apiFetch(`/api/smart-deals/${dealId}`)
        .then(d => {
          setDeal(d);
          requestAnimationFrame(() => window.scrollTo(0, scrollY));
        })
        .catch(() => {});
    }, 5000);
    return () => clearInterval(id);
    // Polling intentionally keys off the status scalar, not the full deal object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, deal?.status]);

  const isClient     = currentUser && deal && String(deal.client_id)     === String(currentUser.user_id);
  const isFreelancer = currentUser && deal && String(deal.freelancer_id)  === String(currentUser.user_id);
  const publicShareUrl = publicTransactionUrl(deal?.share_token || deal?.share_code);

  async function copyShareLink() {
    try {
      await copyTransactionUrl(publicShareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Transaction link is unavailable. Please try again.');
    }
  }

  async function handleAccept() {
    setAccepting(true); setActionError(null);
    try { await apiFetch(`/api/smart-deals/${dealId}/accept`, { method: "POST" }); await load(); }
    catch (e) { setActionError(e.message); } finally { setAccepting(false); }
  }

  async function handleDeliver() {
    if (!window.confirm("Mark work as delivered? The client will be notified to review.")) return;
    setDelivering(true); setActionError(null);
    try { await apiFetch(`/api/smart-deals/${dealId}/deliver`, { method: "POST" }); await load(); }
    catch (e) { setActionError(e.message); } finally { setDelivering(false); }
  }

  async function handleApprove() {
    if (!window.confirm("Approve the work and pay the freelancer? Payouts can take up to 2 business days.")) return;
    setApproving(true); setActionError(null);
    try { await apiFetch(`/api/smart-deals/${dealId}/approve`, { method: "POST" }); await load(); }
    catch (e) { setActionError(e.message); } finally { setApproving(false); }
  }

  async function handleDispute() {
    if (!disputeReason.trim()) return;
    setDisputing(true); setActionError(null);
    try {
      await apiFetch(`/api/smart-deals/${dealId}/dispute`, { method: "POST", body: JSON.stringify({ reason: disputeReason }) });
      setShowDisputeForm(false); await load();
    } catch (e) { setActionError(e.message); } finally { setDisputing(false); }
  }

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
      <div style={{ width: 28, height: 28, border: `3px solid ${D.border}`, borderTopColor: D.blue, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ padding: "14px 16px", borderRadius: 10, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, borderLeft: `3px solid ${D.danger}`, color: D.danger, fontSize: 13 }}>
        {error}
      </div>
    </div>
  );

  if (!deal) return null;

  // Milestone deals render a dedicated view (structure approval + per-milestone flow).
  if (deal.deal_type === "DIGITAL_WORK_MILESTONE" || Array.isArray(deal.milestones)) {
    return (
      <MilestoneDealView
        deal={deal}
        currentUser={currentUser}
        isClient={isClient}
        isFreelancer={isFreelancer}
        reload={load}
      />
    );
  }

  const fmt = v => `R ${Number(v).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", paddingBottom: 40 }}>
      <Link to="/smart-deals" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: D.textMuted, textDecoration: "none", marginBottom: 20 }}>
        <ArrowLeft size={14} /> All deals
      </Link>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: D.text, margin: "0 0 8px", wordBreak: "break-word" }}>{deal.title}</h1>
          <StatusBadge status={deal.status} record={deal} role={isFreelancer ? 'seller' : 'buyer'} />
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: D.accent, fontFamily: D.sans }}>{fmt(deal.amount)}</div>
          <div style={{ fontSize: 11, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.05em" }}>{deal.currency}</div>
        </div>
      </div>

      {actionError && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, borderLeft: `3px solid ${D.danger}`, color: D.danger, fontSize: 13, marginBottom: 14 }}>
          {actionError}
        </div>
      )}

      <ProgressTracker status={deal.status} record={deal} role={isFreelancer ? 'seller' : 'buyer'} />

      {/* Share link — visible to client */}
      {isClient && (
        <div style={card({ marginBottom: 14 })}>
          <p style={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 6px" }}>
            Share with Freelancer
          </p>
          <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 10px" }}>
            Send this link to your freelancer so they can view and accept the deal.
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{
              flex: 1, padding: "9px 12px", borderRadius: 8,
              background: D.bg, border: `1px solid ${D.border}`,
              fontSize: 12, color: D.textMuted,
              fontFamily: D.sans,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {publicShareUrl || 'Transaction link unavailable'}
            </div>
            <button
              onClick={copyShareLink}
              disabled={!publicShareUrl}
              style={{ ...btn(copied ? D.success : D.blue, "#16181B", { flexShrink: 0 }) }}
            >
              {copied ? <><CheckCircle size={13} /> Copied!</> : "Copy link"}
            </button>
            {publicShareUrl && <a
              href={`https://wa.me/?text=${encodeURIComponent(`I've set up a secure TrustTrade deal for you. Click the link to view and confirm it: ${publicShareUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#25D366', color: 'white', padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, borderRadius: 4, textDecoration: 'none', flexShrink: 0 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Share on WhatsApp
            </a>}
          </div>
        </div>
      )}

      {/* ── Action panels ── */}

      {/* Freelancer: accept */}
      {isFreelancer && deal.status === "PENDING" && (
        <ActionCard accent={D.orange}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CheckCircle size={16} color={D.orange} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Accept this deal</h3>
          </div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.5 }}>
            Check the work and amount. Once you accept, the client pays and you can start.
          </p>
          <button onClick={handleAccept} disabled={accepting} style={{ ...btn(D.orange), opacity: accepting ? 0.6 : 1 }}>
            {accepting ? <><Spinner /> Accepting…</> : <>Accept deal <ArrowRight size={14} /></>}
          </button>
        </ActionCard>
      )}

      {/* Client: fund escrow */}
      {isClient && deal.status === "ACCEPTED" && (
        <FundPanel deal={deal} />
      )}

      {/* Client: awaiting payment confirmation */}
      {isClient && deal.status === "PAYMENT_PENDING" && (
        <>
          {/* EFT chosen → keep the bank details + reference card visible the whole time
              the deal waits for payment, so the client always knows where to pay. */}
          {deal.eft_details && (
            <EftDetailsCard details={deal.eft_details} fallbackAmount={deal.total ?? deal.amount} />
          )}
          <ActionCard accent={D.blue}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${D.blue}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Waiting for payment confirmation</h3>
            </div>
            <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.5 }}>
              {deal.eft_details
                ? "Once you've paid using the details above, it can take 1–2 business days to show. The freelancer is told the moment it's in. This page updates on its own."
                : "We're waiting for your payment to come through. Once it's in, the freelancer can start. This page updates on its own."}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {!deal.eft_details && (
                <button
                  type="button"
                  onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&method=${encodeURIComponent(deal.payment_method || 'card')}`)}
                  style={{ ...btn(D.blue, "#16181B") }}
                >
                  <Lock size={14} /> Resume payment
                </button>
              )}
              <button type="button" onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&method=card`)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
                Use Card
              </button>
              <button type="button" onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&method=ozow`)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
                Use Ozow
              </button>
            </div>
          </ActionCard>
        </>
      )}

      {/* Freelancer: mark as delivered */}
      {isFreelancer && deal.status === "FUNDED" && (
        <ActionCard accent={D.purple}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CheckCircle size={16} color={D.purple} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Mark as delivered</h3>
          </div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.5 }}>
            When the work is done, mark it as done. The client reviews and approves to pay you.
          </p>
          <button onClick={handleDeliver} disabled={delivering} style={{ ...btn(D.purple), opacity: delivering ? 0.6 : 1 }}>
            {delivering ? <><Spinner /> Submitting…</> : <><CheckCircle size={14} /> Mark as delivered</>}
          </button>
        </ActionCard>
      )}

      {/* Client: review & approve / dispute */}
      {isClient && deal.status === "DELIVERED" && (
        <ActionCard accent={D.success}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CheckCircle size={16} color={D.success} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Review the work</h3>
          </div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.5 }}>
            Approve to pay the freelancer. Payouts can take up to 2 business days. Not happy? Raise a dispute to pause it.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={handleApprove} disabled={approving} style={{ ...btn(D.success), flex: 1, minWidth: 160, opacity: approving ? 0.6 : 1 }}>
              {approving ? <><Spinner /> Releasing…</> : <><CheckCircle size={14} /> Approve release</>}
            </button>
            <button onClick={() => setShowDisputeForm(v => !v)} style={{ ...btn(D.danger), minWidth: 100 }}>
              <AlertTriangle size={14} /> Dispute
            </button>
          </div>

          {showDisputeForm && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${D.success}33` }}>
              <label htmlFor="deal-field-6" style={{ ...label, marginBottom: 8 }}>Describe the issue</label>
              <textarea id="deal-field-6"
                style={{ ...textarea, marginBottom: 10 }}
                placeholder="What's wrong with the work? Be specific."
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleDispute} disabled={disputing || !disputeReason.trim()} style={{ ...btn(D.danger), opacity: (disputing || !disputeReason.trim()) ? 0.5 : 1 }}>
                  {disputing ? "Submitting…" : "Submit dispute"}
                </button>
                <button onClick={() => setShowDisputeForm(false)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </ActionCard>
      )}

      {/* ── Status alerts ── */}

      {deal.status === "PAYMENT_PENDING" && isFreelancer && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(59,130,246,0.14)", border: `1px solid ${D.blue}44`, borderLeft: `3px solid ${D.blue}`, marginBottom: 14 }}>
          <Clock size={15} color={D.blue} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.blue, margin: 0 }}>The client is paying. We'll let you know once it's in.</p>
        </div>
      )}
      {deal.status === "PENDING" && isClient && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(245,158,11,0.14)", border: `1px solid ${D.warning}44`, borderLeft: `3px solid ${D.warning}`, marginBottom: 14 }}>
          <Clock size={15} color={D.warning} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.warning, margin: 0 }}>Waiting for the freelancer to accept the deal.</p>
        </div>
      )}
      {deal.status === "ACCEPTED" && isFreelancer && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(59,130,246,0.14)", border: `1px solid ${D.blue}44`, borderLeft: `3px solid ${D.blue}`, marginBottom: 14 }}>
          <Clock size={15} color={D.blue} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.blue, margin: 0 }}>Waiting for the client to pay.</p>
        </div>
      )}
      {deal.status === "FUNDED" && isClient && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(139,92,246,0.14)", border: `1px solid ${D.purple}44`, borderLeft: `3px solid ${D.purple}`, marginBottom: 14 }}>
          <Clock size={15} color={D.purple} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.purple, margin: 0 }}>Payment protected. Waiting for the freelancer to do the work.</p>
        </div>
      )}
      {deal.status === "DELIVERED" && isFreelancer && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(16,185,129,0.14)", border: `1px solid ${D.success}44`, borderLeft: `3px solid ${D.success}`, marginBottom: 14 }}>
          <Clock size={15} color={D.success} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.success, margin: 0 }}>Work delivered. Waiting for the client to review and approve.</p>
        </div>
      )}
      {(deal.status === "COMPLETE" || deal.status === "APPROVED") && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(16,185,129,0.14)", border: `1px solid ${D.success}44`, borderLeft: `3px solid ${D.success}`, marginBottom: 14 }}>
          <CheckCircle size={15} color={D.success} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.success, margin: 0, fontWeight: 500 }}>
            {(resolveSmartDealFinancialPresentation(deal, { role: isFreelancer ? 'seller' : 'buyer' }) || {}).label || "Payout Processing"}.
          </p>
        </div>
      )}
      {deal.dispute && (
        <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, borderLeft: `3px solid ${D.danger}`, marginBottom: 14 }}>
          <AlertTriangle size={15} color={D.danger} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontSize: 13, color: D.danger, margin: "0 0 3px", fontWeight: 600 }}>
              Dispute raised {new Date(deal.dispute.raised_at).toLocaleDateString("en-ZA")}
            </p>
            <p style={{ fontSize: 12, color: "#F0665C", margin: 0 }}>{deal.dispute.reason}</p>
          </div>
        </div>
      )}

      {/* Deal details */}
      <div style={card()}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: D.text, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Deal Details
        </h3>
        <InfoRow label="Deal ID"       value={deal.deal_id}    mono />
        <InfoRow label="Client"        value={deal.client_email} />
        <InfoRow label="Freelancer"    value={deal.freelancer_email} />
        <InfoRow label="Amount"        value={fmt(deal.amount)}  mono />
        <InfoRow label="Days to deliver" value={`${deal.days_to_deliver} days`} />
        <InfoRow label="Fee paid by"   value={deal.fee_paid_by === "FREELANCER" ? "Freelancer" : "Client"} />
        <InfoRow label="Created"       value={new Date(deal.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })} />
        {deal.funded_at    && <InfoRow label="Funded"    value={new Date(deal.funded_at).toLocaleDateString("en-ZA",    { day: "numeric", month: "long", year: "numeric" })} />}
        {deal.delivered_at && <InfoRow label="Delivered" value={new Date(deal.delivered_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })} />}

        {deal.description && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${D.surfaceHi}` }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>Scope</p>
            <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>{deal.description}</p>
          </div>
        )}
      </div>

      {/* Message thread */}
      <MessageThread
        dealId={dealId}
        messages={deal.messages || []}
        currentUserId={currentUser?.user_id}
      />

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input:focus,textarea:focus{border-color:${D.blue}!important;outline:none;} *{box-sizing:border-box}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SmartDealList
// ─────────────────────────────────────────────────────────────────────────────

function dealViewerRole(deal, viewer) {
  if (!viewer) return 'buyer';
  const id = String(viewer.user_id || '');
  const email = String(viewer.email || '').trim().toLowerCase();
  const freelancerId = String(deal.freelancer_id || '');
  const freelancerEmail = String(deal.freelancer_email || '').trim().toLowerCase();
  if ((freelancerId && freelancerId === id) || (email && freelancerEmail === email)) return 'seller';
  return 'buyer';
}

export function SmartDealList() {
  const { config: platformConfig } = usePlatformConfig();
  const [deals, setDeals] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showChoice, setShowChoice] = useState(false);
  const navigate = useNavigate();

  const loadDeals = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    return Promise.all([
      apiFetch("/api/smart-deals/"),
      apiFetch("/api/auth/me").catch(() => null),
    ])
      .then(([r, user]) => {
        setDeals(r.deals);
        setViewer(user);
      })
      .catch(() => setLoadError('We could not load your milestone deals. Please try again.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadDeals(); }, [loadDeals]);

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", paddingBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: D.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Lock size={16} color="#16181B" />
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: D.text, margin: 0 }}>Milestone Deals</h1>
          </div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0 }}>Pay in stages as work is completed</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={() => setShowChoice(true)} style={{ ...btn(D.accent, "#16181B") }}>
            + New Deal
          </button>
        </div>
      </div>

      {/* Choice screen — pick the deal type */}
      {showChoice && (
        <div
          onClick={() => setShowChoice(false)}
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(2,6,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ ...card({ marginBottom: 0 }), maxWidth: 460, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: D.text, margin: 0 }}>Start a new deal</h2>
              <button onClick={() => setShowChoice(false)} style={{ background: "none", border: "none", color: D.textMuted, cursor: "pointer", padding: 4 }}>
                <X size={18} />
              </button>
            </div>
            {/* UX-01: phrased so it reads correctly for whichever side opens
                it — not just "how do YOU get paid" (seller-only framing). */}
            <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 16px" }}>How should this deal be paid?</p>

            {[
              { icon: Lock, title: "Single payment", desc: "One payment for the whole job, held protected and released once the work is approved.", to: "/smart-deals/new" },
              platformConfig.milestones_enabled ? { icon: Layers, title: "Paid in stages", desc: "The job is broken into stages. The client funds and approves each stage separately as work is completed.", to: "/smart-deals/new-milestone" } : null,
            ].filter(Boolean).map(opt => (
              <button
                key={opt.to}
                onClick={() => { setShowChoice(false); navigate(opt.to); }}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 12, width: "100%", textAlign: "left",
                  padding: "14px 16px", marginBottom: 10, borderRadius: 12, cursor: "pointer",
                  background: D.surfaceHi, border: `1px solid ${D.border}`, color: D.text,
                }}
              >
                <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: "rgba(59,130,246,0.14)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <opt.icon size={16} color={D.blue} />
                </div>
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: D.text, margin: "0 0 2px" }}>{opt.title}</p>
                  <p style={{ fontSize: 12, color: D.textMuted, margin: 0, lineHeight: 1.5 }}>{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Hero explanation */}
      <div style={{
        background: "linear-gradient(135deg, rgba(59,130,246,0.16) 0%, rgba(30,41,59,0.65) 100%)",
        border: `1px solid ${D.borderLight}`,
        borderRadius: 14,
        padding: "20px 22px",
        marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Zap size={16} color={D.accent} />
          <span style={{ fontSize: 13, fontWeight: 700, color: D.accent, letterSpacing: "0.04em", textTransform: "uppercase" }}>Protected payments, stage by stage</span>
        </div>

        <p style={{ fontSize: 14, color: D.text, lineHeight: 1.65, margin: "0 0 16px" }}>
          Agree on the work and price for each stage. The client funds one stage at a time, and payment is
          released after the completed work is approved.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {[
            { emoji: "🏗️", label: "Building & Construction" },
            { emoji: "💻", label: "Freelance Work" },
            { emoji: "🎨", label: "Design Projects" },
            { emoji: "🔧", label: "Home Renovations" },
          ].map(({ emoji, label }) => (
            <span key={label} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "5px 12px", borderRadius: 20,
              background: "rgba(59,130,246,0.12)",
              border: "1px solid rgba(59,130,246,0.25)",
              fontSize: 12, fontWeight: 500, color: D.text,
            }}>
              {emoji} {label}
            </span>
          ))}
        </div>
        <p style={{ fontSize: 11, color: D.textSoft, margin: "12px 0 0" }}>Pay as the work is done — money is only released once the client approves each stage.</p>
      </div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60 }}>
          <div style={{ width: 28, height: 28, border: `3px solid ${D.border}`, borderTopColor: D.blue, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </div>
      )}

      {!loading && loadError && <ErrorState title="Milestone deals unavailable" body={loadError} action={<SecondaryButton onClick={loadDeals}>Try again</SecondaryButton>} />}
      {!loading && !loadError && deals.length === 0 && (
        <div style={{ ...card({ textAlign: "center", padding: "48px 32px" }) }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: D.surfaceHi, border: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <Briefcase size={22} color={D.textSoft} />
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: D.text, margin: "0 0 6px" }}>No Milestone Deals yet</h3>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 20px" }}>Set up a deal and get paid safely, stage by stage.</p>
          <button onClick={() => setShowChoice(true)} style={btn(D.blue)}>
            <Zap size={14} /> Create your first Milestone Deal
          </button>
        </div>
      )}

      {!loading && !loadError && deals.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {deals.map(d => (
            <div
              key={d.deal_id}
              role="button" tabIndex={0}
              onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate(`/smart-deals/${d.deal_id}`); } }}
              onClick={() => navigate(`/smart-deals/${d.deal_id}`)}
              style={{ ...card({ marginBottom: 0, padding: "24px 26px", cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s" }) }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = D.borderLight; e.currentTarget.style.boxShadow = `0 4px 16px rgba(15,23,42,0.10)`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = D.border; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: D.text, margin: "0 0 4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {d.title}
                  </p>
                  <p style={{ fontSize: 12, color: D.textSoft, margin: 0 }}>
                    {d.client_email} → {d.freelancer_email}
                  </p>
                  {d.deal_type === "DIGITAL_WORK_MILESTONE" && d.milestone_count != null && (
                    <p style={{ fontSize: 11, color: D.accent, margin: "5px 0 0", display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Layers size={11} /> {milestoneListProgressLabel(d)}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: D.accent, margin: "0 0 5px", fontFamily: D.sans }}>
                    R {Number(d.amount).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
                  </p>
                  <StatusBadge status={d.status} record={d} role={dealViewerRole(d, viewer)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Milestone deals — shared helpers + components
// ─────────────────────────────────────────────────────────────────────────────

const fmtZAR = v => `R ${Number(v).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

function milestoneListProgressLabel(deal) {
  const count = Number(deal.milestone_count) || 0;
  const released = Number(deal.milestones_released) || 0;
  const refunded = Number(deal.milestones_refunded) || 0;
  const cancelled = Number(deal.milestones_cancelled) || 0;
  const settled = released + refunded + cancelled;
  if (cancelled && settled === count) {
    if (!released && !refunded) return "Closed before payment";
    const parts = [];
    if (released) parts.push(`${released} released`);
    if (refunded) parts.push(`${refunded} refunded`);
    return `${parts.join(", ")} · project closed`;
  }
  if (refunded) return `${released} released, ${refunded} refunded of ${count}`;
  return `${released} of ${count} released`;
}

// ─── MilestoneFundPanel ───────────────────────────────────────────────────
// Buyer pays a single milestone into escrow. Mirrors FundPanel, scoped to one milestone.

function MilestoneFundPanel({ deal, milestone, reload }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [payConfirm, setPayConfirm] = useState(null);  // { link, total_value, processing_fee }
  const [err, setErr] = useState(null);
  const [eftDetails, setEftDetails] = useState(null);

  // Exact stage cost from the checkout quote (same grant path as /pay).
  // Pay stays disabled until the selected method has a complete quote.
  const [quote, setQuote] = useState(null);
  const payable = payableMethods(quote);
  const canPay = payable.includes(method.toUpperCase());

  async function handleFund() {
    setLoading(true); setErr(null);
    try {
      if (method !== "eft") {
        navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&milestone=${encodeURIComponent(milestone.milestone_id)}&method=${encodeURIComponent(method)}`);
        return;
      }
      const res = await apiFetch(`/api/smart-deals/${deal.deal_id}/milestones/${milestone.milestone_id}/fund`, {
        method: "POST",
        body: JSON.stringify({ payment_method: method }),
      });
      if (res.eft_details) { setEftDetails(res.eft_details); reload && reload(); return; }
      setErr(res.message || "Couldn't start the payment. Please try again or pick another method.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  const confirmAndPay = () => { const pc = payConfirm; if (!pc) return; setPayConfirm(null); window.location.href = pc.link; };

  if (eftDetails) return <EftDetailsCard details={eftDetails} fallbackAmount={milestone.amount} />;

  return (
    <div style={{ marginTop: 4 }}>
      <PaymentConfirmModal open={!!payConfirm} amount={payConfirm?.total_value} processingFee={payConfirm?.processing_fee} onConfirm={confirmAndPay} onCancel={() => setPayConfirm(null)} />
      <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 12px", lineHeight: 1.5 }}>
        Pay for this stage now. <strong style={{ color: D.text }}>{deal.freelancer_name || "The freelancer"}</strong> only
        gets paid once <strong style={{ color: D.text }}>you approve</strong> the work. Payouts can take up to 2 business days.
      </p>

      <div style={{ marginBottom: 14 }}>
        <FullCostQuote dealId={deal.deal_id} milestoneId={milestone.milestone_id} onQuote={setQuote} />
        {payable.length > 0 && (
          <div role="radiogroup" aria-label="Payment method" style={{ display: "flex", gap: 8, marginTop: 10 }}>
            {payable.map(m => (
              <button key={m} type="button" role="radio" aria-checked={method === m.toLowerCase()} data-testid={`stage-method-${m}`}
                onClick={() => setMethod(m.toLowerCase())}
                style={{ ...btn(method === m.toLowerCase() ? D.blue : D.surfaceHi), flex: 1 }}>
                {m === "OZOW" ? "Ozow Instant EFT" : "Card"}
              </button>
            ))}
          </div>
        )}
      </div>

      {err && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, color: D.danger, fontSize: 13, marginBottom: 12 }}>
          {err}
        </div>
      )}

      <button onClick={handleFund} disabled={loading || !canPay} data-testid="stage-pay-btn" style={{ ...btn(D.blue), width: "100%", opacity: loading || !canPay ? 0.6 : 1, cursor: loading || !canPay ? "not-allowed" : "pointer" }}>
        {loading ? <><Spinner /> Setting up payment…</> : <><Lock size={14} /> Continue to secure payment</>}
      </button>
    </div>
  );
}

// ─── ReleaseConfirmModal ────────────────────────────────────────────────────
// Dedicated premium confirmation for the one real money-movement action a
// buyer takes on this page — matches the house modal pattern (see
// PaymentConfirmModal). Replaces window.confirm for this action only;
// deliver/dispute/cancel-payment confirmations are untouched (no financial
// logic here, purely a presentation swap for the highest-stakes action).
//
// PR #73 REVIEW: added keyboard accessibility (Escape to dismiss, a Tab trap
// between the two buttons so focus can't silently leave the dialog onto the
// page behind it, and initial focus on Cancel — the safe default for a
// destructive/financial confirmation, so an accidental Enter press doesn't
// release funds). Duplicate-submission protection for the release call
// itself is a ref guard in releaseNow (below) — synchronous and immune to
// the render-timing race a state-only `busy` check can't fully close.

function ReleaseConfirmModal({ open, milestoneLabel, amount, onConfirm, onCancel, busy }) {
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const handleKeyDown = e => {
    if (e.key === "Escape") {
      if (!busy) onCancel();
      return;
    }
    if (e.key === "Tab") {
      // Only two focusable controls — trap Tab/Shift+Tab between them so
      // focus never silently escapes to the page behind the modal.
      const first = confirmRef.current, last = cancelRef.current;
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  return (
    <div
      onClick={busy ? undefined : onCancel}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed", inset: 0, zIndex: 2000, background: "rgba(5,8,12,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label="Release payment"
        style={{
          width: "100%", maxWidth: 400, background: D.surfaceSolid, border: `1px solid ${D.border}`,
          borderRadius: 16, padding: "26px 24px", boxShadow: D.shadow,
        }}
      >
        <p style={{ fontSize: 17, fontWeight: 700, color: D.text, margin: "0 0 14px", lineHeight: 1.4 }}>
          Release payment for {milestoneLabel}?
        </p>
        <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 8px", lineHeight: 1.6 }}>
          You are confirming that this stage is complete.
        </p>
        <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 8px", lineHeight: 1.6 }}>
          The protected <strong style={{ color: D.text }}>{fmtZAR(amount)}</strong> payment for this stage will be released to the seller.
        </p>
        <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 22px", lineHeight: 1.6 }}>
          This action cannot be undone.
        </p>
        <button ref={confirmRef} onClick={onConfirm} disabled={busy} style={{ ...btn(D.accent, "#0B0D10"), width: "100%", opacity: busy ? 0.6 : 1 }}>
          {busy ? <><Spinner /> Releasing…</> : <>Release {fmtZAR(amount)}</>}
        </button>
        <button ref={cancelRef} onClick={onCancel} disabled={busy} style={{
          width: "100%", marginTop: 10, background: "transparent", color: D.textMuted,
          border: `1px solid ${D.border}`, borderRadius: 10, padding: "11px 18px",
          fontSize: 14, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit",
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── MilestoneCard ──────────────────────────────────────────────────────────
// The "current stage" hero card when isCurrent — the protected-payment
// control centre for ONE stage. Also reused (unchanged) inside each
// CompactMilestoneRow's own disclosure for completed/upcoming stages.
// Hierarchy comes from typography and spacing, not coloured borders — see
// the premium milestone-page redesign notes on MS_STATUS above.

function MilestoneCard({ deal, milestone, isClient, isFreelancer, reload, isCurrent, totalStages, structureApproved = true }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [confirmingRelease, setConfirmingRelease] = useState(false);
  // Synchronous re-entrancy guard for the one real money-movement action
  // here — a ref (not state) so it's immune to the render-timing race a
  // `busy` state check alone can't fully close (two calls in the same tick,
  // before React has committed the disabled button, would both see the same
  // stale `busy`). The modal's disabled buttons remain the primary UX
  // guard for ordinary double-clicks; this closes the narrow gap under it.
  const releasingRef = useRef(false);

  const financial = resolveSmartDealFinancialPresentation(milestone, { role: isFreelancer ? 'seller' : 'buyer' });
  const s = {
    ...(MS_STATUS[milestone.status] || { label: milestone.status, color: D.textSoft, dot: D.textSoft }),
    ...(financial ? { label: financial.label } : {}),
  };
  const base = `/api/smart-deals/${deal.deal_id}/milestones/${milestone.milestone_id}`;
  const sellerName = deal.freelancer_name || "the seller";
  const buyerName = deal.client_name || "the client";
  const settled = Boolean(confirmedSettlementAt(milestone));

  async function act(path, label, body, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(label); setErr(null);
    try {
      await apiFetch(`${base}${path}`, { method: "POST", ...(body ? { body: JSON.stringify(body) } : {}) });
      await reload();
    } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }

  async function releaseNow() {
    if (releasingRef.current) return;
    releasingRef.current = true;
    setBusy("approve"); setErr(null);
    try {
      await apiFetch(`${base}/approve`, { method: "POST" });
      setConfirmingRelease(false);
      await reload();
    } catch (e) { setConfirmingRelease(false); setErr(e.message); }
    finally { setBusy(null); releasingRef.current = false; }
  }

  return (
    <div style={{
      background: isCurrent ? D.surfaceHi : D.surface,
      border: `1px solid ${D.border}`,
      borderRadius: 16,
      padding: isCurrent ? "22px 24px" : "16px 18px",
      marginBottom: 12,
      boxShadow: isCurrent ? "0 12px 32px rgba(0,0,0,0.28)" : "none",
    }}>
      <ReleaseConfirmModal
        open={confirmingRelease}
        milestoneLabel={`Stage ${milestone.seq}`}
        amount={milestone.amount}
        busy={busy === "approve"}
        onConfirm={releaseNow}
        onCancel={() => setConfirmingRelease(false)}
      />

      <div className="tt-stage-heading" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: isCurrent ? 16 : 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isCurrent ? (
            <p style={{ fontSize: 10, fontWeight: 800, color: D.accent, textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 7px" }}>
              Current stage
            </p>
          ) : (
            <p style={{ fontSize: 10, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 4px" }}>
              Stage {milestone.seq}
            </p>
          )}
          <p style={{ fontSize: isCurrent ? 21 : 14, fontWeight: 700, color: D.text, margin: 0, lineHeight: 1.3, wordBreak: "break-word" }}>
            {milestone.description}
          </p>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          {isCurrent && totalStages > 1 && (
            <p style={{ fontSize: 10, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 7px" }}>
              Stage {milestone.seq} of {totalStages}
            </p>
          )}
          <p style={{ fontSize: isCurrent ? 21 : 15, fontWeight: 700, color: D.text, margin: 0, fontFamily: D.sans }}>
            {fmtZAR(milestone.amount)}
          </p>
        </div>
      </div>

      <span className="tt-stage-status" style={{
        display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600,
        color: s.color, marginBottom: isCurrent ? 16 : 4,
      }}>
        {milestone.status === 'PROPOSED' ? <Lock size={11} aria-label="Not yet available" /> : <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />}
        {s.label}
      </span>

      {err && (
        <div style={{ padding: "9px 12px", borderRadius: 8, background: "rgba(240,102,92,0.10)", border: `1px solid ${D.danger}33`, color: D.danger, fontSize: 12, margin: "0 0 12px" }}>
          {err}
        </div>
      )}

      {/* Buyer: pay this milestone */}
      {isClient && milestone.status === "AWAITING_PAYMENT" && (
        <MilestoneFundPanel deal={deal} milestone={milestone} reload={reload} />
      )}
      {isFreelancer && milestone.status === "AWAITING_PAYMENT" && (
        <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>Waiting for {buyerName} to fund this stage.</p>
      )}

      {/* Buyer: awaiting payment confirmation */}
      {isClient && milestone.status === "PAYMENT_PENDING" && (
        <div>
          {milestone.eft_details && <EftDetailsCard details={milestone.eft_details} fallbackAmount={milestone.amount} />}
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.6 }}>
            {milestone.eft_details
              ? "Once you've paid using the details above, it can take 1–2 business days to show. This page updates on its own."
              : "Waiting for your payment to come through. This page updates on its own."}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {!milestone.eft_details && (
              <button
                type="button"
                onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&milestone=${encodeURIComponent(milestone.milestone_id)}&method=${encodeURIComponent(milestone.payment_method || 'card')}`)}
                style={{ ...btn(D.accent, "#0B0D10") }}
              >
                <Lock size={14} /> Resume payment
              </button>
            )}
            <button type="button" onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&milestone=${encodeURIComponent(milestone.milestone_id)}&method=card`)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
              Use Card
            </button>
            <button type="button" onClick={() => navigate(`/pay?deal=${encodeURIComponent(deal.deal_id)}&milestone=${encodeURIComponent(milestone.milestone_id)}&method=ozow`)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
              Use Ozow
            </button>
          </div>
        </div>
      )}
      {isFreelancer && milestone.status === "PAYMENT_PENDING" && (
        <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>The client is paying for this stage. We'll let you know once it's paid.</p>
      )}

      {/* Payment protected / released trust block — buyer and seller alike */}
      {milestone.status === "FUNDED" && (
        <div style={{ padding: isCurrent ? "14px 16px" : "10px 12px", borderRadius: 12, background: "rgba(61,209,148,0.08)", marginBottom: isFreelancer ? 14 : 0 }}>
          <p style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: D.success, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 4px" }}>
            <Shield size={13} /> Payment protected
          </p>
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
            {fmtZAR(milestone.amount)} is protected through our payment provider while this stage is in progress.
          </p>
        </div>
      )}
      {isClient && milestone.status === "FUNDED" && (
        <p style={{ fontSize: 13, color: D.textMuted, margin: "10px 0 0", lineHeight: 1.6 }}>Waiting for {sellerName} to complete this stage.</p>
      )}

      {/* Seller: funded, awaiting delivery */}
      {isFreelancer && milestone.status === "FUNDED" && (
        <div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 14px", lineHeight: 1.6 }}>
            The client has funded this stage. Complete the agreed work, then mark it ready for review.
          </p>
          <button onClick={() => act("/deliver", "deliver", null, "Mark this stage ready for review? We'll let the client know to review it.")}
            disabled={busy === "deliver"} style={{ ...btn(D.accent, "#0B0D10"), opacity: busy === "deliver" ? 0.6 : 1 }}>
            {busy === "deliver" ? <><Spinner /> Submitting…</> : <>Mark stage ready for review</>}
          </button>
        </div>
      )}

      {/* Seller: delivered, waiting for client. FLOW-01: nothing releases
          this automatically — there is no live auto-approval after the
          stored review window (confirmed in routes/smart_deals.py:
          auto_approve_expired_deals is explicitly "NOT used in production"
          and is never called by any scheduler). The payment stays
          protected indefinitely.
          Final Milestone Support Safety Fix (2026-10-13): this used to
          open the AI SupportChat widget, which cannot escalate to a human
          — it can only advise the user to email support themselves.
          "Request support with this stage" instead opens the real human-
          reaching channel (FeedbackButton -> POST /feedback, which emails
          an actual admin inbox), pre-filled with the project and milestone
          identifiers so the seller doesn't have to type them. */}
      {isFreelancer && milestone.status === "DELIVERED" && (
        <div>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 6px", lineHeight: 1.6 }}>Waiting for {buyerName} to approve or raise a dispute.</p>
          <p style={{ fontSize: 12, color: D.textSoft, margin: 0, lineHeight: 1.6 }}>
            Payment stays protected while you wait. If {buyerName} is unresponsive,{" "}
            <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("trusttrade:open-feedback", {
              detail: {
                heading: `Request support with Stage ${milestone.seq}`,
                prefill: `I need help with Stage ${milestone.seq} (${milestone.description}) of "${deal.title}" (${deal.deal_id}). The client has not responded since I marked this stage ready for review.\n\n`,
              },
            }))}
              style={{ background: "none", border: "none", color: D.blue, fontWeight: 600, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
              request support with this stage
            </button>.
          </p>
        </div>
      )}

      {/* Buyer: approve / dispute */}
      {isClient && milestone.status === "DELIVERED" && (
        <div>
          {isCurrent && (
            <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 16px", lineHeight: 1.6 }}>
              {sellerName} has delivered this stage. Review the completed work before releasing payment.
            </p>
          )}
          {isCurrent && (
            <p style={{ fontSize: 10, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>
              What to do now
            </p>
          )}
          <button onClick={() => setConfirmingRelease(true)}
            disabled={busy === "approve"} style={{ ...btn(D.accent, "#0B0D10"), width: "100%", opacity: busy === "approve" ? 0.6 : 1, marginBottom: 10 }}>
            {busy === "approve" ? <><Spinner /> Releasing…</> : <>Approve &amp; release payment</>}
          </button>
          <div style={{ textAlign: "center" }}>
            <button onClick={() => setShowDispute(v => !v)} style={{
              background: "none", border: "none", color: D.textSoft, fontSize: 12, fontWeight: 600,
              cursor: "pointer", padding: "4px 8px", fontFamily: "inherit",
            }}>
              Raise a dispute
            </button>
          </div>
          {showDispute && (
            <div style={{ marginTop: 10, padding: "14px 16px", borderRadius: 12, background: "rgba(240,102,92,0.08)" }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: D.text, margin: "0 0 6px" }}>Raise a dispute</p>
              <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 4px", lineHeight: 1.6 }}>
                Use this if the delivered work does not match what was agreed.
              </p>
              <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 10px", lineHeight: 1.6 }}>
                Payment for this stage remains protected while the dispute is reviewed.
              </p>
              <label htmlFor="deal-field-7" style={{ ...label, marginBottom: 6 }}>What's wrong with this stage?</label>
              <textarea id="deal-field-7" style={{ ...textarea, marginBottom: 10 }} placeholder="Be specific so we can help resolve it quickly."
                value={disputeReason} onChange={e => setDisputeReason(e.target.value)} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => act("/dispute", "dispute", { reason: disputeReason })}
                  disabled={busy === "dispute" || disputeReason.trim().length < 10}
                  style={{ ...btn(D.danger), opacity: (busy === "dispute" || disputeReason.trim().length < 10) ? 0.5 : 1 }}>
                  {busy === "dispute" ? "Submitting…" : "Submit dispute"}
                </button>
                <button onClick={() => setShowDispute(false)} style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }) }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Released — trust block already shown above for FUNDED only; RELEASED gets its own */}
      {milestone.status === "RELEASED" && (
        <div style={{ padding: isCurrent ? "14px 16px" : "10px 12px", borderRadius: 12, background: "rgba(61,209,148,0.08)" }}>
          <p style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: D.success, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 4px" }}>
            <CheckCircle size={13} /> {settled ? "Payment released" : s.label || "Payment released"}
          </p>
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
            {settled
              ? "Bank settlement is confirmed for this stage."
              : "Payment released. Bank settlement is pending confirmation."}
          </p>
        </div>
      )}

      {/* Refunded — a dispute resolved in the buyer's favour. Terminal and
          finished like RELEASED, but deliberately neutral, never the green
          success treatment — this stage was not completed successfully. */}
      {milestone.status === "REFUNDED" && (
        <div style={{ padding: isCurrent ? "14px 16px" : "10px 12px", borderRadius: 12, background: D.surfaceHi }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 4px" }}>Refunded</p>
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
            This stage's dispute was resolved in the buyer's favour. The payment was refunded, not released.
          </p>
        </div>
      )}

      {milestone.status === "CANCELLED" && (
        <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
          This stage was closed before it was paid. Nothing was charged for it.
        </p>
      )}

      {/* UX-10: before approval every stage is PROPOSED (not just later
          ones), and the instruction must never tell the seller they'll be
          paying. "The previous stage" framing is only ever true once the
          project itself has been agreed to — before that, nothing has a
          "previous stage" yet. */}
      {milestone.status === "PROPOSED" && (
        <p style={{ fontSize: 13, color: D.textSoft, margin: 0, lineHeight: 1.6 }}>
          {!structureApproved
            ? (isFreelancer
                ? "Waiting for the client to approve the project and fund the first stage."
                : "Review the project above, then agree to the stages to get started.")
            : (isFreelancer
                ? "This stage opens once the previous stage is approved."
                : "You'll fund this once the previous stage is approved.")}
        </p>
      )}

      {milestone.status === "DISPUTED" && (
        <div style={{ padding: isCurrent ? "14px 16px" : "10px 12px", borderRadius: 12, background: "rgba(240,102,92,0.08)" }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: D.danger, textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 4px" }}>Under review</p>
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
            Payment for this stage remains protected while this is reviewed.
            {milestone.dispute?.reason ? ` Reason: ${milestone.dispute.reason}` : ""}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── CompactMilestoneRow ───────────────────────────────────────────────────
// A single-line summary for a stage that is NOT the current one — RELEASED
// (completed, compact) or not yet reached (upcoming, quiet). Keeps the
// current stage dominant without hiding the others: each stays reachable
// through its own disclosure, which renders the full MilestoneCard.

// A clean compact row, no bordered box — a subtle bottom divider separates
// rows instead. Released (terminal, successful) stages show a quiet green
// check; a refunded stage is also terminal/finished but must never read as
// a successful release — neutral dot, muted text, no green check, no
// "Completed" framing. Upcoming stages are muted grey. Click to expand the
// exact same MilestoneCard, so nothing becomes unreachable.
function CompactMilestoneRow({ deal, milestone, isClient, isFreelancer, reload, state, isLast, structureApproved = true }) {
  const terminal = state === "completed";
  const pending = state === "pending";
  const refunded = milestone.status === "REFUNDED";
  const cancelled = milestone.status === "CANCELLED";
  const quietTerminal = refunded || cancelled;
  const financial = resolveSmartDealFinancialPresentation(milestone, { role: isFreelancer ? 'seller' : 'buyer' });
  // UX-11: before the client has agreed to the project, every stage is
  // "pending agreement" — not "Upcoming" (which implies the project is
  // already under way and this stage is simply next in line).
  const statusLabel = pending ? "Pending agreement" : (financial?.label || MS_STATUS[milestone.status]?.label || milestone.status);
  return (
    <details style={{ borderBottom: isLast ? "none" : `1px solid ${D.border}` }}>
      <summary style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "12px 2px", cursor: "pointer", listStyle: "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {quietTerminal
            ? <span style={{ width: 15, height: 15, borderRadius: "50%", background: D.textMuted, flexShrink: 0 }} />
            : terminal
              ? <CheckCircle size={15} color={D.success} style={{ flexShrink: 0 }} />
              : <span style={{ width: 15, height: 15, borderRadius: "50%", border: `1.5px solid ${D.border}`, flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <p style={{
              fontSize: 13, fontWeight: 600, color: quietTerminal ? D.textMuted : terminal ? D.text : D.textMuted, margin: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              Stage {milestone.seq} · {milestone.description}
            </p>
            <p style={{ fontSize: 11, color: quietTerminal ? D.textMuted : D.textSoft, margin: "1px 0 0" }}>{statusLabel}</p>
          </div>
        </div>
        <span style={{ fontSize: 13, color: D.textMuted, fontFamily: D.sans, flexShrink: 0 }}>{fmtZAR(milestone.amount)}</span>
      </summary>
      <div style={{ padding: "0 0 14px" }}>
        <MilestoneCard deal={deal} milestone={milestone} isClient={isClient} isFreelancer={isFreelancer} reload={reload} structureApproved={structureApproved} />
      </div>
    </details>
  );
}

// ─── MilestoneDealView ────────────────────────────────────────────────────

function MilestoneDealView({ deal, currentUser, isClient, isFreelancer, reload }) {
  const [approvingStructure, setApprovingStructure] = useState(false);
  const [closingProject, setClosingProject] = useState(false);
  const [actionError, setActionError] = useState(null);

  const milestones = [...(deal.milestones || [])].sort((a, b) => (a.seq || 0) - (b.seq || 0));
  // RELEASED and REFUNDED are both terminal — a refund (dispute resolved in
  // the buyer's favour) finishes a stage exactly like a release does, and
  // the backend opens the next milestone either way (see
  // _MILESTONE_TERMINAL_STATUSES in routes/smart_deals.py). Only RELEASED
  // counts as a genuine success for progress wording, though — a project
  // with any refund is "resolved", never flatly "complete".
  const terminalCount = milestones.filter(m => MS_TERMINAL_STATUSES.has(m.status)).length;
  const hasRefund = milestones.some(m => m.status === "REFUNDED");
  const hasCancelled = milestones.some(m => m.status === "CANCELLED");
  const progressWord = hasRefund ? "resolved" : hasCancelled ? "settled" : "complete";
  const canCloseUnpaid = milestones.some(m => m.status === "PROPOSED" || m.status === "AWAITING_PAYMENT");
  const paymentInFlight = milestones.some(m => m.status === "PAYMENT_PENDING");
  const sellerName = deal.freelancer_name || deal.freelancer_email || "The seller";
  const buyerName = deal.client_name || deal.client_email || "The client";
  // UX-11: the project hasn't been agreed to yet while structure_status is
  // still PROPOSED — Stage 1 must not be presented as the actionable
  // "current stage" before the client has approved anything (it was
  // previously picked purely from milestone status, which is PROPOSED for
  // every stage pre-approval, so index 0 always "won" — a false positive).
  // Once approved, the current stage dominates the page exactly as before;
  // every terminal (RELEASED or REFUNDED) stage is compact, everything
  // after the current one is "upcoming" (quiet) — see CompactMilestoneRow.
  const structureApproved = deal.structure_status === "APPROVED";
  const currentIdx = structureApproved ? milestones.findIndex(m => !MS_TERMINAL_STATUSES.has(m.status)) : -1;
  const current = currentIdx !== -1 ? milestones[currentIdx] : null;

  async function approveStructure() {
    if (!window.confirm("Agree to these project stages? This does not charge you — you'll pay for Stage 1 separately.")) return;
    setApprovingStructure(true); setActionError(null);
    try { await apiFetch(`/api/smart-deals/${deal.deal_id}/approve-structure`, { method: "POST" }); await reload(); }
    catch (e) { setActionError(e.message); } finally { setApprovingStructure(false); }
  }

  async function closeRemaining() {
    if (!window.confirm("Close every stage that has not been paid? Funded stages stay protected. This does not move any money.")) return;
    setClosingProject(true); setActionError(null);
    try { await apiFetch(`/api/smart-deals/${deal.deal_id}/close-remaining`, { method: "POST" }); await reload(); }
    catch (e) { setActionError(e.message); } finally { setClosingProject(false); }
  }

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "0 4px 48px" }}>
      <Link to="/smart-deals" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: D.textMuted, textDecoration: "none", marginBottom: 28 }}>
        <ArrowLeft size={14} /> All project deals
      </Link>

      {/* 1. Project header — one balanced block, no duplicate progress badge. */}
      <div className="tt-deal-heading" style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: D.text, margin: "0 0 4px", wordBreak: "break-word", letterSpacing: "-0.01em" }}>
          {deal.title}
        </h1>
        <p style={{ fontSize: 13, color: D.textSoft, margin: "0 0 16px" }}>Protected milestone project</p>
        <div className="tt-deal-meta" style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
          <div>
            <p style={{ fontSize: 16, fontWeight: 700, color: D.text, margin: 0, fontFamily: D.sans }}>{fmtZAR(deal.amount)} <span style={{ fontSize: 12, fontWeight: 500, color: D.textSoft }}>total</span></p>
            <p style={{ fontSize: 12, color: D.textSoft, margin: "2px 0 0" }}>{milestones.length} {milestones.length === 1 ? "stage" : "stages"}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, color: D.textSoft, margin: 0 }}>Client</p>
            <p style={{ fontSize: 13, color: D.textMuted, margin: "2px 0 0" }}>{buyerName}</p>
          </div>
          <div>
            <p style={{ fontSize: 11, color: D.textSoft, margin: 0 }}>Seller</p>
            <p style={{ fontSize: 13, color: D.textMuted, margin: "2px 0 0" }}>{sellerName}</p>
          </div>
        </div>
      </div>

      {actionError && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(240,102,92,0.10)", color: D.danger, fontSize: 13, marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      {/* Buyer: review & approve the structure (plain-English invite copy).
          UX-12: the full agreement — every stage, amount, total and fee
          arrangement — is visible here, above the approval action, not
          just referenced. UX-13: "Agree to project stages", not
          "Approve & start" — and explicitly says this doesn't charge the
          client. UX-14: a plain secondary way to ask for changes instead
          of approving, via the existing message thread. */}
      {isClient && deal.status === "PROPOSED" && (
        <div style={{ background: D.surfaceHi, borderRadius: 16, padding: "20px 22px", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Shield size={16} color={D.accent} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: D.text, margin: 0 }}>Review the project</h3>
          </div>
          <p style={{ fontSize: 11, fontWeight: 700, color: D.accent, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" }}>
            Awaiting your approval
          </p>
          <p style={{ fontSize: 13, color: D.textMuted, margin: "0 0 16px", lineHeight: 1.6 }}>
            <strong style={{ color: D.text }}>{sellerName}</strong> has sent you a project in {milestones.length} stages.
            You pay one stage at a time, and the payment is protected until you confirm each stage's work is done.
          </p>

          {/* UX-12: every stage description, amount and the total — visible
              before the approval button, not hidden below it. */}
          <div style={{ padding: "12px 14px", borderRadius: 10, background: D.bg, margin: "0 0 12px" }}>
            {deal.description && (
              <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 10px", lineHeight: 1.6 }}>{deal.description}</p>
            )}
            {milestones.map(m => (
              <div key={m.milestone_id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderBottom: `1px solid ${D.border}` }}>
                <span style={{ fontSize: 12, color: D.textMuted }}>Stage {m.seq} · {m.description}</span>
                <span style={{ fontSize: 12, color: D.text, fontFamily: D.sans, flexShrink: 0 }}>{fmtZAR(m.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: D.text }}>Total</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: D.text, fontFamily: D.sans }}>{fmtZAR(deal.amount)}</span>
            </div>
          </div>

          <div style={{ padding: "12px 14px", borderRadius: 10, background: D.bg, margin: "0 0 16px" }}>
            <p style={{ fontSize: 12, color: D.textSoft, margin: "0 0 5px", lineHeight: 1.5 }}>You fund one stage at a time.</p>
            <p style={{ fontSize: 12, color: D.textSoft, margin: "0 0 5px", lineHeight: 1.5 }}>
              TrustTrade's fee is 2% of each stage. You pay it — the seller is not charged.
            </p>
            <p style={{ fontSize: 12, color: D.textSoft, margin: 0, lineHeight: 1.5 }}>
              Payment-provider processing applies separately when each stage is funded. The final amount is confirmed before each payment.
            </p>
          </div>

          <p style={{ fontSize: 12, color: D.textSoft, margin: "0 0 12px", lineHeight: 1.5 }}>
            Approving the project does not charge you. You'll pay for Stage 1 separately.
          </p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={approveStructure} disabled={approvingStructure} style={{ ...btn(D.accent, "#0B0D10"), opacity: approvingStructure ? 0.6 : 1 }}>
              {approvingStructure ? <><Spinner /> Approving…</> : <>Agree to project stages</>}
            </button>
            <button
              type="button"
              onClick={() => document.getElementById("tt-deal-messages")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              style={{ background: "none", border: "none", color: D.textSoft, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "4px 8px", fontFamily: "inherit" }}
            >
              Ask for changes
            </button>
          </div>
        </div>
      )}

      {/* Seller: waiting for buyer approval */}
      {isFreelancer && deal.status === "PROPOSED" && (
        <div style={{ display: "flex", gap: 10, padding: "14px 16px", borderRadius: 12, background: D.surfaceHi, marginBottom: 24 }}>
          <Clock size={15} color={D.textSoft} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 3px" }}>
              Awaiting client approval
            </p>
            <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
              Waiting for {buyerName} to approve the project and fund the first stage.
            </p>
          </div>
        </div>
      )}

      {/* Completed banner — genuine full success only. */}
      {deal.status === "COMPLETE" && (
        <div style={{ display: "flex", gap: 10, padding: "14px 16px", borderRadius: 12, background: "rgba(61,209,148,0.08)", marginBottom: 24 }}>
          <CheckCircle size={15} color={D.success} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.success, margin: 0, fontWeight: 600 }}>
            {(resolveSmartDealFinancialPresentation(deal, { role: isFreelancer ? 'seller' : 'buyer' }) || {}).label || "Payout Processing"}.
          </p>
        </div>
      )}

      {/* Resolved-with-refund banner — deliberately NOT the green success
          treatment above. Every stage is finished, but not every stage was
          released, so this must never claim normal project completion. */}
      {deal.status === "COMPLETE_WITH_REFUND" && (
        <div style={{ display: "flex", gap: 10, padding: "14px 16px", borderRadius: 12, background: D.surfaceHi, marginBottom: 24 }}>
          <Shield size={15} color={D.textMuted} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, fontWeight: 600 }}>
            This project is resolved. One or more stages were refunded rather than released.
            {hasCancelled ? " Unpaid stages were closed and were not charged." : ""}
          </p>
        </div>
      )}

      {(deal.status === "CLOSED" || deal.status === "CANCELLED") && (
        <div style={{ display: "flex", gap: 10, padding: "14px 16px", borderRadius: 12, background: D.surfaceHi, marginBottom: 24 }}>
          <Shield size={15} color={D.textMuted} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 13, color: D.textMuted, margin: 0, fontWeight: 600 }}>
            {deal.status === "CANCELLED"
              ? "This project was closed before any stage was paid. Nothing was charged."
              : "This project is closed. Released stages stay paid. Stages that were never paid were not charged."}
          </p>
        </div>
      )}

      {(canCloseUnpaid || paymentInFlight) && (isClient || isFreelancer) && deal.status !== "CANCELLED" && deal.status !== "CLOSED" && (
        <div style={{ padding: "14px 16px", borderRadius: 12, background: D.surfaceHi, border: `1px solid ${D.border}`, marginBottom: 24 }}>
          <p style={{ fontSize: 13, color: D.text, margin: "0 0 6px", fontWeight: 600 }}>Stop after the current work</p>
          <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 12px", lineHeight: 1.6 }}>
            {paymentInFlight
              ? "A stage payment is still processing, so the rest of the project can't be closed yet."
              : "Closes every stage that has not been paid. Stages already funded stay protected until they are approved or disputed. This does not move any money."}
          </p>
          {canCloseUnpaid && !paymentInFlight && (
            <button type="button" onClick={closeRemaining} disabled={closingProject}
              style={{ ...btn("transparent", D.textMuted, { border: `1px solid ${D.border}` }), opacity: closingProject ? 0.6 : 1 }}>
              {closingProject ? "Closing…" : "Close unpaid stages"}
            </button>
          )}
        </div>
      )}

      {/* 2. Project progress — a compact segmented rail, not an empty bar card. */}
      {milestones.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>Project progress</p>
            <p style={{ fontSize: 12, color: D.textMuted, margin: 0 }}>{terminalCount} of {milestones.length} {progressWord}</p>
          </div>
          <div className="tt-progress-rail" style={{ display: "flex", alignItems: "flex-start" }}>
            {milestones.map((m, i) => {
              // Four distinct, deliberate colours: RELEASED = green
              // (genuinely completed), REFUNDED = neutral grey (finished,
              // but not a success), CURRENT = TrustTrade blue, UPCOMING =
              // muted outline. A refund must never read as a green success.
              const released = m.status === "RELEASED";
              const refunded = m.status === "REFUNDED";
              const cancelled = m.status === "CANCELLED";
              const isCurrentDot = i === currentIdx;
              const prev = i > 0 ? milestones[i - 1] : null;
              const lineColor = !prev ? D.border
                : prev.status === "REFUNDED" || prev.status === "CANCELLED" ? D.textMuted
                : MS_TERMINAL_STATUSES.has(prev.status) ? D.success
                : D.border;
              return (
                <div key={m.milestone_id} style={{ display: "flex", alignItems: "flex-start", flex: i === milestones.length - 1 ? "0 0 auto" : 1, minWidth: 0 }}>
                  {i > 0 && <div style={{ flex: 1, height: 2, marginTop: 5, minWidth: 10, background: lineColor }} />}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, flexShrink: 0, padding: "0 2px", minWidth: 0 }}>
                    <span style={{
                      width: 11, height: 11, borderRadius: "50%", flexShrink: 0,
                      background: released ? D.success : (refunded || cancelled) ? D.textMuted : isCurrentDot ? D.accent : D.surfaceHi,
                      border: released || refunded || cancelled || isCurrentDot ? "none" : `1.5px solid ${D.border}`,
                      boxShadow: isCurrentDot ? `0 0 0 4px ${D.accent}22` : "none",
                    }} />
                    <span className="tt-progress-rail-label" style={{
                      fontSize: 11, color: isCurrentDot ? D.text : D.textSoft, fontWeight: isCurrentDot ? 700 : 500,
                      whiteSpace: "nowrap", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {m.description}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {current && (
            <p style={{ fontSize: 12, color: D.textMuted, margin: "16px 0 0" }}>
              Current: Stage {current.seq} — {MS_STATUS[current.status]?.label || current.status}
            </p>
          )}
        </div>
      )}

      {/* 3. Current stage hero card + 4. Other stages. */}
      {current && (
        <MilestoneCard deal={deal} milestone={current} isClient={isClient} isFreelancer={isFreelancer} reload={reload} isCurrent totalStages={milestones.length} structureApproved={structureApproved} />
      )}

      {milestones.some((m, i) => i !== currentIdx) && (
        <div style={{ marginTop: current ? 28 : 0, marginBottom: 28 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 4px" }}>
            {structureApproved ? "Other stages" : "Project stages"}
          </p>
          {milestones.map((m, i) => (
            i === currentIdx ? null : (
              <CompactMilestoneRow key={m.milestone_id} deal={deal} milestone={m} isClient={isClient} isFreelancer={isFreelancer} reload={reload} structureApproved={structureApproved}
                state={MS_TERMINAL_STATUSES.has(m.status) ? "completed" : structureApproved ? "upcoming" : "pending"} isLast={i === milestones.length - 1} />
            )
          ))}
        </div>
      )}

      {/* 5. Project details — collapsed, never competing with the current stage. */}
      <details style={{ marginBottom: 20 }}>
        <summary style={{
          display: "flex", alignItems: "center", gap: 4, cursor: "pointer", listStyle: "none",
          fontSize: 13, fontWeight: 600, color: D.textMuted, padding: "6px 2px", width: "fit-content",
        }}>
          Project details <ChevronRight size={14} className="tt-details-chevron" />
        </summary>
        <div style={{ padding: "8px 2px 4px" }}>
          <InfoRow label="Project ID" value={deal.deal_id} mono />
          <InfoRow label="Total value" value={fmtZAR(deal.amount)} mono />
          <InfoRow label="Buyer" value={deal.client_email} />
          <InfoRow label="Seller" value={deal.freelancer_email} />
          <InfoRow label="TrustTrade fee arrangement" value={deal.fee_paid_by === "FREELANCER" ? "Freelancer" : "Client"} />
          <InfoRow label="Created" value={new Date(deal.created_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })} />
          {deal.description && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${D.surfaceHi}` }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>Scope</p>
              <p style={{ fontSize: 13, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>{deal.description}</p>
            </div>
          )}
        </div>
      </details>

      {/* 6. Messages / supporting content. Anchored so "Ask for changes" above
          can scroll straight to it (UX-14). */}
      <div id="tt-deal-messages">
        <MessageThread dealId={deal.deal_id} messages={deal.messages || []} currentUserId={currentUser?.user_id} />
      </div>

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        input:focus,textarea:focus{border-color:${D.blue}!important;outline:none;}
        *{box-sizing:border-box}
        details > summary::-webkit-details-marker{display:none}
        details[open] .tt-details-chevron{transform:rotate(90deg)}
        .tt-details-chevron{transition:transform 0.15s}
        @media(max-width:480px){.tt-progress-rail-label{display:none}}
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateMilestoneDeal (seller-initiated)
// ─────────────────────────────────────────────────────────────────────────────

// UX-02: deliverable-based examples instead of a payment-percentage example
// ("50% deposit" describes money, not work) — shown as rotating PLACEHOLDER
// text per stage, never pre-filled into the field itself.
const MILESTONE_DESCRIPTION_EXAMPLES = [
  "e.g. Initial design and client review",
  "e.g. Final approved files delivered",
  "e.g. Describe the deliverable for this stage",
];

// UX-08: a single source of truth for amount validity, used for both the
// live inline warning and the disabled-submit check — "enter a valid
// positive number of at least R500" covers empty/negative/decimal/non-
// numeric entries without inventing a second validation path.
function milestoneAmountIssue(value) {
  if (value === "" || value === null || value === undefined) return "Enter an amount";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a valid number";
  if (n <= 0) return "Enter an amount greater than R0";
  if (n < MIN_TRANSACTION_AMOUNT) return `Minimum R${MIN_TRANSACTION_AMOUNT} per stage`;
  return null;
}

// Illustrative-only example, collapsed by default — never touches form state
// (Final UX Polish, 2026-10-14, Section 2). Plain <details>/<summary>, same
// accessible/no-JS-state pattern already used for "Project details" above.
const EXAMPLE_ROWS = [
  { stage: 1, work: "Materials delivered and checked", amount: 2000 },
  { stage: 2, work: "Installation completed", amount: 5000 },
  { stage: 3, work: "Final inspection approved", amount: 1000 },
];

function MilestoneExampleDisclosure() {
  return (
    <details style={{ marginBottom: 18 }}>
      <summary style={{
        display: "flex", alignItems: "center", gap: 4, cursor: "pointer", listStyle: "none",
        fontSize: 13, fontWeight: 600, color: D.accent, padding: "6px 2px", width: "fit-content",
      }}>
        See an example — Home renovation <ChevronRight size={14} className="tt-ex-chevron" />
      </summary>
      <div style={{ padding: "10px 2px 2px" }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: D.textMuted, margin: "0 0 8px" }}>Example project: R8,000</p>
        <div style={{ overflowX: "auto", marginBottom: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${D.borderLight}` }}>
                <th style={{ textAlign: "left", padding: "6px 8px", color: D.textSoft, fontWeight: 600 }}>Stage</th>
                <th style={{ textAlign: "left", padding: "6px 8px", color: D.textSoft, fontWeight: 600 }}>Work</th>
                <th style={{ textAlign: "right", padding: "6px 8px", color: D.textSoft, fontWeight: 600 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {EXAMPLE_ROWS.map(r => (
                <tr key={r.stage} style={{ borderBottom: `1px solid ${D.borderLight}` }}>
                  <td style={{ padding: "6px 8px", color: D.textMuted }}>{r.stage}</td>
                  <td style={{ padding: "6px 8px", color: D.text }}>{r.work}</td>
                  <td style={{ padding: "6px 8px", color: D.text, textAlign: "right", whiteSpace: "nowrap" }}>{fmtZAR(r.amount)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ padding: "6px 8px" }} />
                <td style={{ padding: "6px 8px", color: D.text, fontWeight: 700 }}>Total</td>
                <td style={{ padding: "6px 8px", color: D.text, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>{fmtZAR(EXAMPLE_ROWS.reduce((s, r) => s + r.amount, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12.5, color: D.textMuted, margin: "0 0 8px", lineHeight: 1.6 }}>
          Your client funds each stage before work begins. The payment stays protected until the agreed work for that stage has been completed and approved.
        </p>
        <p style={{ fontSize: 12.5, color: D.textSoft, margin: 0, lineHeight: 1.6 }}>
          This is not an upfront deposit. For materials that must be purchased before work starts, the client can arrange to purchase them directly from the supplier.
        </p>
      </div>
      <style>{`details > summary::-webkit-details-marker{display:none} details[open] .tt-ex-chevron{transform:rotate(90deg)} .tt-ex-chevron{transition:transform 0.15s}`}</style>
    </details>
  );
}

export function CreateMilestoneDeal() {
  const navigate = useNavigate();
  const { config: platformConfig, loading: platformLoading } = usePlatformConfig();
  const [form, setForm] = useState({ title: "", description: "", buyer_email: "", fee_paid_by: "CLIENT" });
  const [milestones, setMilestones] = useState([
    { description: "", amount: "" },
    { description: "", amount: "" },
  ]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);
  if (platformLoading || !platformConfig.milestones_enabled) {
    return (
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px" }} data-testid="milestones-unavailable">
        <h1 style={{ fontSize: 22, fontWeight: 700, color: D.text, margin: "0 0 8px" }}>Milestone deals are not available</h1>
        <p style={{ fontSize: 14, color: D.textMuted, margin: 0, lineHeight: 1.5 }}>
          New staged deals cannot be created. A milestone deal that already exists can still be opened, funded, and managed.
        </p>
      </div>
    );
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setMs = (i, k) => e => setMilestones(ms => ms.map((m, idx) => idx === i ? { ...m, [k]: e.target.value } : m));
  const addMs = () => setMilestones(ms => ms.length < 20 ? [...ms, { description: "", amount: "" }] : ms);
  const removeMs = i => setMilestones(ms => ms.length > 1 ? ms.filter((_, idx) => idx !== i) : ms);
  const markTouched = k => setTouched(t => ({ ...t, [k]: true }));

  // UX-08: negative/non-numeric amounts must never be summed into the
  // running total — only genuinely valid positive numbers count.
  const total = milestones.reduce((sum, m) => {
    const n = Number(m.amount);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  const anyAmountInvalid = milestones.some(m => milestoneAmountIssue(m.amount) !== null);

  function validate() {
    const e = {};
    if (!form.title.trim()) e.title = "Required";
    if (!form.buyer_email.includes("@")) e.buyer_email = "Enter a valid email";
    milestones.forEach((m, i) => {
      if (!m.description.trim()) e[`ms_desc_${i}`] = "Describe this milestone";
      const issue = milestoneAmountIssue(m.amount);
      if (issue) e[`ms_amt_${i}`] = issue;
    });
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      // Surface every stage's inline amount warning immediately, not only
      // once each field has individually been blurred.
      setTouched(t => ({ ...t, ...Object.fromEntries(milestones.map((_, i) => [`ms_amt_${i}`, true])) }));
      return;
    }
    setLoading(true); setApiError(null);
    try {
      const res = await apiFetch("/api/smart-deals/milestone-deals", {
        method: "POST",
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          buyer_email: form.buyer_email,
          fee_paid_by: form.fee_paid_by,
          milestones: milestones.map(m => ({ description: m.description, amount: Number(m.amount) })),
        }),
      });
      navigate(`/smart-deals/${res.deal_id}`);
    } catch (err) {
      // UX-03: the backend already returns a clear, actionable message for
      // an unregistered client email ("No TrustTrade account found for that
      // email. Ask your client to sign up first.") — surfaced verbatim.
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const fieldErr = k => errors[k] ? <p style={{ fontSize: 11, color: D.danger, margin: "2px 0 8px" }}>{errors[k]}</p> : null;
  const borderErr = k => errors[k] ? D.danger : D.border;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", paddingBottom: 40 }}>
      <Link to="/smart-deals" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: D.textMuted, textDecoration: "none", marginBottom: 20 }}>
        <ArrowLeft size={14} /> All deals
      </Link>

      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: D.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Layers size={17} color="#16181B" />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: D.text, margin: 0 }}>New Project Deal</h1>
        </div>
      </div>

      {/* How Milestone Deals Work — plain-English explainer (Final UX Polish, 2026-10-14) */}
      <div style={{ padding: "14px 16px", borderRadius: 12, background: "linear-gradient(135deg, rgba(59,130,246,0.14) 0%, rgba(30,41,59,0.6) 100%)", border: `1px solid ${D.borderLight}`, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Zap size={15} color={D.accent} style={{ flexShrink: 0 }} />
          <h3 style={{ fontSize: 14, fontWeight: 700, color: D.text, margin: 0 }}>How Milestone Deals Work</h3>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { n: 1, title: "Agree on the stages", body: "Break the project into smaller parts, each with a clear description and price." },
            { n: 2, title: "Your client funds each stage", body: "Your client pays for one stage at a time. The money is securely held by our third-party payment provider while the work is completed." },
            { n: 3, title: "Get paid when the work is approved", body: "Once your client approves the completed stage, the payment is released through our payment provider. Bank settlement is tracked separately." },
          ].map(step => (
            <div key={step.n} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: "50%", background: "rgba(47,155,255,0.18)", color: D.accent, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{step.n}</span>
              <p style={{ fontSize: 13, color: D.text, margin: 0, lineHeight: 1.55 }}>
                <span style={{ fontWeight: 600 }}>{step.title}</span> — <span style={{ color: D.textMuted }}>{step.body}</span>
              </p>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11.5, color: D.textSoft, margin: "10px 0 0", paddingTop: 10, borderTop: `1px solid ${D.borderLight}` }}>
          Minimum R500 per stage. Your client funds one stage at a time.
        </p>
      </div>

      <MilestoneExampleDisclosure />

      {apiError && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(239,68,68,0.14)", border: `1px solid ${D.danger}44`, borderLeft: `3px solid ${D.danger}`, color: D.danger, fontSize: 13, marginBottom: 14 }}>
          {apiError}
        </div>
      )}

      <div style={card()}>
        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-8" style={label}>Deal Title</label>
          <input id="deal-field-8" style={{ ...input, borderColor: borderErr("title") }} placeholder="e.g. Website build for Acme Co" value={form.title} onChange={set("title")} />
          {fieldErr("title")}
        </div>

        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-9" style={label}>Overview (optional)</label>
          <textarea id="deal-field-9" style={{ ...textarea }} placeholder="A short summary of the whole project…" value={form.description} onChange={set("description")} />
        </div>

        <div style={{ marginBottom: 4 }}>
          <label htmlFor="deal-field-10" style={label}>Client Email</label>
          <input id="deal-field-10" style={{ ...input, borderColor: borderErr("buyer_email") }} type="email" placeholder="client@example.com" value={form.buyer_email} onChange={set("buyer_email")} />
          <p style={{ fontSize: 11, color: D.textSoft, margin: "4px 0 8px", lineHeight: 1.5 }}>
            Your client must have a TrustTrade account to receive this deal.
          </p>
          {fieldErr("buyer_email")}
        </div>

        {/* Milestones */}
        <label style={{ ...label, marginTop: 8 }}>Stages</label>
        <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 4px", lineHeight: 1.5 }}>
          Describe what you'll deliver at each stage and how much it costs. Your client approves the completed
          work before payment is released.
        </p>
        <p style={{ fontSize: 11.5, color: D.textSoft, margin: "0 0 12px", lineHeight: 1.5 }}>
          Example: Materials delivered and checked — R2,000
        </p>

        {milestones.map((m, i) => {
          const amountIssue = milestoneAmountIssue(m.amount);
          const showAmountIssue = touched[`ms_amt_${i}`] && amountIssue;
          return (
            <div key={i} style={{ background: D.surfaceHi, border: `1px solid ${D.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.06em" }}>Stage {i + 1}</span>
                {milestones.length > 1 && (
                  <button onClick={() => removeMs(i)} style={{ background: "none", border: "none", color: D.textMuted, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: 0 }}>
                    <Trash2 size={13} /> Remove
                  </button>
                )}
              </div>
              <input
                style={{ ...input, marginBottom: 8, borderColor: borderErr(`ms_desc_${i}`) }}
                placeholder={MILESTONE_DESCRIPTION_EXAMPLES[i] || MILESTONE_DESCRIPTION_EXAMPLES[MILESTONE_DESCRIPTION_EXAMPLES.length - 1]}
                value={m.description} onChange={setMs(i, "description")} onBlur={() => markTouched(`ms_desc_${i}`)}
              />
              {fieldErr(`ms_desc_${i}`)}
              <input
                style={{ ...input, fontFamily: D.sans, fontWeight: 600, marginBottom: 4, borderColor: showAmountIssue ? D.danger : borderErr(`ms_amt_${i}`) }}
                type="number" min={MIN_TRANSACTION_AMOUNT} placeholder="Amount (ZAR)" value={m.amount}
                onChange={setMs(i, "amount")} onBlur={() => markTouched(`ms_amt_${i}`)}
                aria-invalid={!!showAmountIssue} aria-describedby={`ms-amt-hint-${i}`}
              />
              <p id={`ms-amt-hint-${i}`} style={{ fontSize: 11, color: showAmountIssue ? D.danger : D.textSoft, margin: "4px 0 0" }}>
                {showAmountIssue ? amountIssue : `Minimum R${MIN_TRANSACTION_AMOUNT} per stage`}
              </p>
            </div>
          );
        })}

        {milestones.length < 20 && (
          <button onClick={addMs} style={{ ...btn("transparent", D.accent, { border: `1px dashed ${D.accent}66`, width: "100%", marginBottom: 14 }) }}>
            <Plus size={14} /> Add another stage
          </button>
        )}

        {/* Running total */}
        <div style={{ background: D.bg, border: `1px solid ${D.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: D.text }}>Total deal value</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: D.accent, fontFamily: D.sans }}>{fmtZAR(total)}</span>
          </div>
          <p style={{ fontSize: 11, color: D.textMuted, margin: "6px 0 0" }}>The total of all stages. Your client pays one stage at a time.</p>
        </div>

        {/* UX-04/05: ONE consolidated, accurate fee section — replaces the
            old two-box version, which repeated itself and claimed a single
            payment offers "the same protection" as staged payment. Single-
            payment and milestone arrangements have different funding/
            release structures, so that equivalence claim is gone; this
            section states only what's true. TEMPORARY SAFE LAUNCH POLICY
            (2026-10-13): Milestone Deals are client-pays only — provider
            processing cost differs per payment method/checkout and can't be
            guessed upfront, so a freelancer-pays milestone deal (which
            would deduct that guess straight from the seller's real payout)
            isn't offered; the backend rejects it even if it were. */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ ...label, marginBottom: 10 }}>How payments and fees work</label>
          <div style={{ padding: "12px 14px", borderRadius: 10, border: `1px solid ${D.border}`, background: D.surfaceHi }}>
            <p style={{ fontSize: 12, color: D.textMuted, margin: "0 0 8px", lineHeight: 1.6 }}>
              Your client pays for each stage separately. TrustTrade's platform fee is allocated across the
              project, and the client pays it — you are not charged. Payment-provider processing fees apply
              separately to each stage. The final payable amount must be shown at secure checkout before the
              client authorises payment.
            </p>
            <p style={{ fontSize: 12, color: D.textMuted, margin: 0, lineHeight: 1.6 }}>
              For a deal where you (the freelancer) pay the fee instead, use a{" "}
              <Link to="/transactions/new" onClick={() => trackStartTransaction({ source: "smart_deal_fee_tip" })} style={{ color: D.blue, fontWeight: 600 }}>single payment transaction</Link> instead.
            </p>
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading || anyAmountInvalid} style={{ ...btn(D.accent, "#16181B"), width: "100%", opacity: (loading || anyAmountInvalid) ? 0.6 : 1, cursor: (loading || anyAmountInvalid) ? "not-allowed" : "pointer" }}>
          {loading ? <><Spinner /> Sending…</> : <><Layers size={14} /> Send project for client approval</>}
        </button>
        <p style={{ fontSize: 11, color: D.textSoft, margin: "8px 0 0", textAlign: "center", lineHeight: 1.5 }}>
          This does not charge your client — they'll review and approve the project first.
        </p>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input:focus,textarea:focus{border-color:${D.blue}!important;box-shadow:0 0 0 3px ${D.blue}22;} *{box-sizing:border-box}`}</style>
    </div>
  );
}

export default SmartDealDetail;
